import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStorePogGrading } from "src/components/PlanogramExtensions/storePogRelease";
import styled from "styled-components";
import { PrimitiveAtom, useAtom, useSetAtom, useStore } from "jotai";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { studioItemsOf } from "../adapters/studioItemsOf";
import { Button, Flex, Icon, Spinner, Text, Toggle } from "src/elements";
import { useTranslation } from "src/i18n";
import { downloadFile } from "src/utils/downloadFile";
import {
  describeShipFailure,
  shipCdtCompression,
  unplacedCombosOf,
  unplacedProductCodesOf,
} from "../store/cdtCompressionService";
import type { UnplacedCdtCombo } from "@CommonApi/merchflows/_merchflowId_/cdt/compression";
import { saveVariantRestrictions } from "../store/variantRestrictionService";
import {
  agreeToRestrictions,
  applyRestrictionsThenGenerate,
  declineRestrictions,
  proposeRestrictionsForAll,
  type RestrictionContribution,
  type RestrictionProposal,
} from "../domain/variantRestrictionUpload";
import {
  buildRestrictionOffers,
  offerBaselineOf,
  placementRuleIdOf,
} from "../domain/restrictionOffer";
import { applyPromotion, promotionOn } from "../domain/cdtLayout/restrictionRemedy";
import { applyPromotion as adoptPromotion, revertPromotion } from "../domain/wizardPromotion";
import { withLiveSectionSemantics } from "../adapters/liveSectionSemantics";
import { prepareCompressionPayloadWithCoverage } from "../adapters/toCompressionPayload";
import { renderedProjectionOf } from "../adapters/renderedLayout";
import { canvasIdsOfSkus } from "@CommonUtils/cdtStudio/adapters/pogToStudioItems";
import { toCdtAssignmentsCsv } from "../adapters/toCdtAssignmentsCsv";
import {
  restrictionsToFileRows,
  toRestrictionsDetailCsv,
  toVariantRestrictionsFileCsv,
} from "../adapters/toRestrictionsCsv";
import { finalizeCdts } from "../domain/finalize";
import { withProfileLevels } from "../domain/collapse4";
import { WallReadingLine } from "./WallReadingLine";
import { shipDraftOf } from "../domain/adoptMeasuredBands";
import {
  closestBaseMatches,
  storeRangeProductsOf,
  type PlacedStoreProduct,
} from "../domain/coverStoreRange";
import { cdt0ResolutionOf } from "../domain/cdt0Resolution";
import {
  acceptFlag,
  answerSizeLayout,
  answerSizeTier,
  applyFix,
  clearSizeLayoutAnswer,
  dismissFlag,
  isFlagSettled,
  unansweredSizeLayoutFlags,
  withdrawSizeTierAnswer,
} from "../domain/studioTransactions";
import { SIZE_LAYOUT_PREFIX, type SizeLayout } from "../domain/sizeTiers";
import {
  applyHierarchyMove,
  budgetQuestionOf,
  siblingMoveOf,
  type MoveReport,
  type MoveResult,
} from "../domain/hierarchyMoves/applyHierarchyMove";
import { poolingKeyOf } from "../domain/cdtLayout/buildFromCdt";
import { poolingQuestionOf } from "../domain/cdtLayout/poolingQuestion";
import {
  endPanelPreviewSession,
  type FixStep,
  getPanelPreviewState,
  initPanelPreviewState,
  resetPanelPreviewState,
  setPanelFixes,
  setPanelPreviewOn,
  standingFixesOf,
} from "../store/panelPreviewState";
import { historyPreviewFollowUpFor, pogAuthorOf } from "../store/previewReconcile";
import { studioIsDirty } from "../store/studioDirty";
import { clearStudioHistory, studioHistoryKey } from "../store/studioHistoryStore";
import {
  declineQuestions,
  getDeclinedQuestions,
  undeclineQuestions,
} from "../store/studioQuestionAnswers";
import { useModals } from "src/components/Modals/store/hooks";
import { useToasts } from "src/components/Toasts/store/hooks";
import { cdtStudioLatestReleaseAtom, type ShippedRelease } from "../store/blockingMode";
import { Color } from "src/elements/Color/Color";
import { buildLayout } from "../domain/buildLayout";
import {
  closeFillWhereShort,
  holdsAgainst,
  improvesOn,
  raisesTile,
  samePlacements,
  stampLayout,
  treeEditHarms,
  verdictOfLayout,
  type PlanVerdict,
} from "../domain/planVerdict";
import {
  FLOW_CARD_PREFIX,
  cardFixOf,
  cardRefusalOf,
  draftFixOf,
  fixAllOnce,
  fillCardWordsOf,
  piecesCardWordsOf,
  raisedCardsOf,
  readPlan,
  sameTreeForFixes,
  squaredBeforeFixesOf,
  verticalOfferOf,
  type FixSetting,
  type RaisedCard,
  type PlanFix,
  type PlanReading,
  type VerticalOffer,
} from "../domain/planFixes";
import { setBrandShelves } from "../domain/horizontalBlocking";
import { RESOLVER_FLAG_KINDS } from "../resolver/toResolverInputs";
import { aestheticsScopeOf } from "@CommonUtils/cdtStudio/domain/pogAesthetics";
import { useInference } from "../resolver/useInference";
import { ALERT_KINDS, type InferenceException } from "../resolver/inferenceExceptions";
import {
  PATH_SEPARATOR,
  lengthLabel,
  levelsForScope,
  type LengthUnit,
} from "../domain/studioTypes";
import { effectiveAttrs } from "@CommonUtils/cdtStudio/domain/studioTypes";
import {
  fixedBlockOf,
  listedBlocksOf,
  shortFlagTitle,
  withBlocksCounted,
} from "../domain/shortFlagTitle";
import { detailText, evidenceText, phraseText } from "../domain/flagWords";
import { categoryPrefixOf, pluralOf, withoutCategory } from "../domain/plainNames";
import type {
  Flag,
  FlagSeverity,
  GeneratedLayout,
  RestrictionPlacement,
  StudioDraft,
  StudioItem,
} from "../domain/studioTypes";
import { gateOwnershipLinesOf, shelfSpanLabel } from "../domain/gateOwnership";
import {
  flagIdsOfQuestionId,
  groupStudioQuestions,
  isQuestionFlag,
  type StudioQuestion,
} from "../domain/studioQuestions";
import type { StudioTreeNode } from "../projections/treeProjection";
import {
  cdtStudioActionRequestAtom,
  cdtStudioExceptionCardsAtom,
  cdtStudioHighlightAtom,
  cdtStudioPinsAtom,
  cdtStudioSelectedFlagAtom,
  cdtStudioSelectionSourceAtom,
  sameHighlightIds,
  sameStudioCards,
  sameStudioPins,
  type CdtStudioExceptionCard,
  type CdtStudioPin,
} from "../store/atoms";
import { useStudioAttributesQuery } from "../store/useStudioAttributesQuery";
import { useStudioSession } from "../store/useStudioSession";
import {
  namedStudioConfigurationOf,
  type NamedStudioConfiguration,
} from "../store/studioNamingProfile";
import {
  type StudioCategoryConfiguration,
  type StudioCategoryIdentity,
} from "../store/studioCategoryConfiguration";
import { STUDIO_THEME } from "../constants/studioTheme";
import { MoveReportSummary } from "./MoveReportSummary";
import {
  StudioTreeCanvas,
  StudioTreeFoldLine,
  wallCountsOf,
  type FoldCount,
} from "./StudioTreeCanvas";
import { planSlacksOf } from "../domain/bayShare";
import { useCategoryLengthUnit } from "../store/useCategoryLengthUnit";

/**
 * Opens Generate Store Planograms. A Finalize passes the release it just shipped, so the dialog
 * defaults to it (Category plan CDT, and the blocking the release reads as) without a refetch.
 */
export type OpenGenerate = (finalized?: ShippedRelease) => Promise<unknown>;

interface Props {
  pogAtom: PrimitiveAtom<Pog>;
  isEditable: boolean;
  /** Scopes the item master. Without it two flows share one cache entry. */
  merchflowId?: number;
  onClose?: () => void;
  onGenerate?: OpenGenerate;
}

interface CdtStudioPanelProps extends Props {
  basePogId?: number;
  categoryIdentity?: StudioCategoryIdentity;
  categoryConfiguration: StudioCategoryConfiguration;
}

/**
 * A shelf band's row words, "<section> · <form>": the section is the value the band's promotion
 * splits, the form the value the band was found on (the promotion's `fromAttr`, read off the
 * band's own products). A band offered with no promotion is a whole block: its value, and no
 * form (`null`, which the row words as "whole block"). A form the same as its section is named
 * once, so a row never reads "Tide · Tide".
 */
export const bandWordsOfOffer = (
  offer: {
    bandVariant: string;
    skuIds: readonly string[];
    promotion?: { parentValue: string; fromAttr: string };
  },
  items: readonly { skuId: string; attrs: Record<string, string | null> }[],
): { section: string; form: string | null } => {
  const { promotion } = offer;
  if (!promotion) return { section: offer.bandVariant, form: null };
  const section = promotion.parentValue;
  const owned = new Set(offer.skuIds);
  const found = items
    .filter((item) => owned.has(item.skuId))
    .map((item) => item.attrs[promotion.fromAttr])
    .find((value): value is string => typeof value === "string" && value.trim() !== "");
  const form =
    found ??
    (offer.bandVariant.startsWith(`${section} `)
      ? offer.bandVariant.slice(section.length + 1)
      : offer.bandVariant);
  return form === section ? { section: "", form } : { section, form };
};

/**
 * The section a product stands in on the base: its top-level value on the draft `baseline`
 * (the draft with the kept carves undone, `offerBaselineOf`), so a carved corner is not taken
 * for a section of its own.
 */
export const sectionOnBaseOf = (item: StudioItem, baseline: StudioDraft): string | undefined => {
  const attr = baseline.spine[0]?.attr;
  if (attr === undefined) return undefined;
  const value = effectiveAttrs(item, baseline)[attr];
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
};

/**
 * The shelf bands by section, for the card's intro: a promoted band in the section its
 * promotion splits, a whole-block band in the section its products stand in on the base
 * (`sectionOf`, by product). Null where a band's section cannot be read, so the intro never
 * lists counts that do not add up.
 */
export const bandSectionsOf = (
  offers: readonly {
    bandVariant: string;
    skuIds: readonly string[];
    promotion?: { parentValue: string };
  }[],
  sectionOf: (skuId: string) => string | undefined,
): { section: string; count: number }[] | null => {
  const counts = new Map<string, number>();
  for (const offer of offers) {
    const section =
      offer.promotion?.parentValue ??
      offer.skuIds.map(sectionOf).find((value) => value !== undefined);
    if (section === undefined || section === "") return null;
    counts.set(section, (counts.get(section) ?? 0) + 1);
  }
  return [...counts.entries()].map(([section, count]) => ({ section, count }));
};

/** What a blocking exception is, for the amber line's words: a row with gaps, a product
 *  standing outside its block, or anything else Studio can fix. */
export type ExceptionKind = "gap" | "outside" | "other";
export const exceptionKindOf = (flag: Flag): ExceptionKind =>
  fillCardWordsOf(flag) ? "gap" : flag.kind === "orphan" ? "outside" : "other";

export const staleRuleNamesOf = (
  stale: readonly { restriction: { cdtPath: readonly string[] } }[],
): string => {
  const names = stale
    .map((entry) => entry.restriction.cdtPath[entry.restriction.cdtPath.length - 1]?.trim() ?? "")
    .filter((name) => name !== "");
  return [...new Set(names)].join(", ");
};

/** A sentence's first letter in capitals: a list of answers stands at the start of one. */
const capitalized = (text: string) => text.charAt(0).toLocaleUpperCase() + text.slice(1);

/** Exceptions the amber line names when there are several; the rest are counted. */
const EXCEPTION_TITLES_SHOWN = 3;

/** Blocking exceptions whose fixes the panel works out unasked, for the amber line. */
const EXCEPTION_FIXES_WORKED_OUT = 10;
/** Joins flag ids into one dependency: a character no flag id carries. */
const PAIR_IDS_SEPARATOR = "\u001f";

/** Fixes Fix all names in its note; the rest are counted, so the note stays one line. */
const FIX_ALL_NAMES_SHOWN = 3;

/** Fix all's note on the fixes it kept, given by name: every fix counted, as the move modal
 *  counts the fixes it discards, and each name once, as a card worked again once the plan
 *  changed under it is named once. Where a name repeats, the note says how many different ones
 *  there were, so the names shown and the "more" add up to a number on the note. */
export const fixAllNoteOf = (
  t: ReturnType<typeof useTranslation<"cdtStudio">>["t"],
  kept: readonly string[],
  scores: { scoreBefore: number | string; scoreAfter: number | string },
): string => {
  const names = [...new Set(kept)];
  const shown = names.slice(0, FIX_ALL_NAMES_SHOWN).join("; ");
  const fixes =
    names.length > FIX_ALL_NAMES_SHOWN
      ? t("resolver.fixAllMore", { names: shown, count: names.length - FIX_ALL_NAMES_SHOWN })
      : shown;
  const params = { ...scores, count: kept.length, distinct: names.length, fixes };
  return names.length === kept.length
    ? t(`resolver.fixAllDone_${pluralOf(kept.length)}`, params)
    : t(`resolver.fixAllDoneRepeats_${pluralOf(names.length)}`, params);
};

/** A verdict's score as the tile shows it. */
const shownScore = (verdict: { score: number }) =>
  Number.isFinite(verdict.score) ? Math.round(verdict.score) : "—";

/** Why a tree edit is refused (see `treeEditHarms`): a plan the tile cannot grade, or harm. */
const treeEditRefusalKey = (laid: PlanVerdict) =>
  Number.isNaN(laid.score) ? "resolver.treeEditUnscored" : "resolver.treeEditHarms";

/** The same refusal on the preview toggle, which changes no tree. */
const previewRefusalKey = (laid: PlanVerdict) =>
  Number.isNaN(laid.score) ? "panel.previewUnscored" : "panel.previewHarms";

/** What a tree edit costs in score, for the confirm modal; null when it costs nothing. */
const scoreCostOf = (standing: PlanVerdict, laid: PlanVerdict) =>
  laid.score < standing.score
    ? { scoreBefore: shownScore(standing), scoreAfter: shownScore(laid) }
    : null;

/** A control that waits while Fix all runs or a move is weighed. A disabled button takes no
 *  pointer, so the reason it waits is on this wrapper. */
const Waits = ({ reason, children }: { reason?: string; children: ReactNode }) => (
  <span title={reason}>{children}</span>
);

const SeverityChip = styled.span<{ $severity: FlagSeverity | "resolved" }>`
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  border: 1px solid ${({ $severity }) => STUDIO_THEME.severity[$severity].border};
  background: ${({ $severity }) => STUDIO_THEME.severity[$severity].background};
  color: ${({ $severity }) => STUDIO_THEME.severity[$severity].text};
  font-size: 0.625rem;
  font-weight: 800;
  white-space: nowrap;
`;

const FindingRow = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  padding: 0.5rem;
  border: 1px solid ${({ $selected }) => ($selected ? STUDIO_THEME.primary : STUDIO_THEME.border)};
  border-radius: 0.375rem;
  background: ${STUDIO_THEME.canvasBackground};
  cursor: pointer;
  text-align: left;
`;

const QuestionCard = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;
  padding: 0.625rem;
  border: 1px solid ${({ $selected }) => ($selected ? STUDIO_THEME.primary : STUDIO_THEME.border)};
  border-radius: 0.375rem;
  background: ${STUDIO_THEME.canvasBackground};
`;

const TidyUpsRow = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem;
  border: 1px solid ${STUDIO_THEME.border};
  border-radius: 0.375rem;
  background: ${STUDIO_THEME.surface};
  cursor: pointer;
  text-align: left;
`;

/** The chevron that folds a question card to its title. */
const CardFold = styled.button`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0.125rem;
  border: none;
  background: transparent;
  cursor: pointer;
`;

/** One answer of a layout question: a radio and its words, the whole row clickable. */
const AnswerRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.5rem;
  border: 1px solid ${STUDIO_THEME.border};
  border-radius: 0.25rem;
  cursor: pointer;
`;

/** The shelf bands card's columns: band, shelves, bays, Show me. */
const BandGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 6.5rem 5.5rem auto;
  align-items: center;
  gap: 0.5rem;
`;

const BandRow = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.375rem 0.25rem;
  border-top: 1px solid ${STUDIO_THEME.border};
  background: ${({ $selected }) => ($selected ? STUDIO_THEME.surface : "transparent")};
`;

const BandName = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  cursor: pointer;
`;

const SizeTagsLine = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;
  padding: 0.625rem;
  border: 1px solid ${STUDIO_THEME.border};
  border-radius: 0.375rem;
  background: ${STUDIO_THEME.canvasBackground};
`;

/** The blocking exceptions Studio can fix, in one amber line. */
const ExceptionLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin: 0 0.75rem 0.5rem 0.75rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${STUDIO_THEME.severity.toCheck.border};
  border-radius: 0.375rem;
  background: ${STUDIO_THEME.severity.toCheck.background};
`;

/** The panel's foot, always in view: the one primary button and what it waits on. */
const PanelFooter = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-top: 1px solid ${STUDIO_THEME.border};
  background: ${STUDIO_THEME.canvasBackground};
`;

/** The shelf bands card's id among the folded and numbered cards. */
const BANDS_CARD_ID = "placement-bands";
/** The size tags' line, among the numbered cards. */
const SIZE_TAGS_CARD_ID = "size-tags";

/** The header's More menu: a small box under the header, right-aligned. */
const MoreMenu = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-end;
  gap: 0.25rem;
  margin: 0.25rem 0.75rem;
  padding: 0.375rem 0.5rem;
  border: 1px solid ${STUDIO_THEME.border};
  border-radius: 0.375rem;
  background: ${STUDIO_THEME.canvasBackground};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transform: rotate(${({ $open }) => ($open ? 90 : 0)}deg);
  transition: transform 0.15s ease;
`;

const FindingsScroller = styled(Flex)`
  flex-direction: column;
  flex-grow: 1;
  gap: 0.375rem;
  overflow-y: auto;
  min-height: 0;
  padding: 0 0.75rem 0.75rem 0.75rem;
`;

/** Beside the open tree the questions take 40% of the panel; with the tree folded, the rest. */
const FindingsRegion = styled(Flex)<{ $fill: boolean }>`
  flex: ${({ $fill }) => ($fill ? "1 1 auto" : "0 0 40%")};
  flex-direction: column;
  min-height: 8rem;
  max-height: ${({ $fill }) => ($fill ? "none" : "40%")};
  overflow: hidden;
  /* Its own stacking context: the tree's positioned layers above it never paint over the cards. */
  position: relative;
  z-index: 1;
  border-top: 1px solid ${STUDIO_THEME.border};
  background: ${STUDIO_THEME.canvasBackground};
`;

const severityOrder: FlagSeverity[] = ["blocking", "toCheck", "toReview"];

/** Keyed by pogAtom in store/panelPreviewState: the true original pog survives tab switches. */
const StudioPanelSession = ({
  pogAtom,
  isEditable,
  merchflowId,
  basePogId,
  categoryId,
  onClose,
  onGenerate,
  configuration,
  lengthUnit,
  storeCodes = [],
}: Props & {
  basePogId: number | null;
  categoryId: number | null;
  configuration: NamedStudioConfiguration;
  /** The category's unit, confirmed by its release before the session opens. */
  lengthUnit: LengthUnit;
  storeCodes?: readonly string[];
}) => {
  const { t, tDynamic } = useTranslation("cdtStudio");
  const { closeModal, openConfirmModal } = useModals();
  const { toast } = useToasts();
  const [latestRelease, setLatestRelease] = useAtom(cdtStudioLatestReleaseAtom);
  const hasRelease =
    latestRelease?.basePogId === basePogId && typeof latestRelease.releaseId === "number";
  const [pog, setPog] = useAtom(pogAtom);
  const pogStore = useStore();
  const setHighlights = useSetAtom(cdtStudioHighlightAtom);

  // The studio's frozen input. On remount the atom may hold an applied preview, which must
  // NOT become the new original.
  const [originalPog, setOriginalPog] = useState<Pog>(() => {
    const persisted = initPanelPreviewState(pogAtom as object, pog);
    // With no preview standing, a changed atom was rewritten externally: adopt it as the
    // new original. With a preview standing the difference is ours, so leave it to close.
    const external = persisted.originalPog !== pog && !persisted.previewOn;
    if (!external) return persisted.originalPog;
    return resetPanelPreviewState(pogAtom as object, pog).originalPog;
  });
  // The plan the session reads: the original until the tree is moved, then the plan the
  // move laid, so the exceptions raised and the fixes made are those of the plan on screen.
  const [workingPog, setWorkingPog] = useState<Pog>(() =>
    getPanelPreviewState(pogAtom as object)?.previewOn ? pog : originalPog,
  );
  // A remount over the studio's own plan takes up the fixes standing on it, so Close still
  // asks before it discards them and Undo and Reset fixes still reach them.
  const [resumedFixes] = useState(() => standingFixesOf(pogAtom as object));
  const pogs = useMemo(() => [workingPog], [workingPog]);
  // The questions are about the plan the studio opened on, whatever plan the session works on.
  const asBuiltPogs = useMemo(() => [originalPog], [originalPog]);
  /** Names as the category manager reads them: sections without the category before them. */
  const categoryPrefix = useMemo(
    () =>
      categoryPrefixOf(
        originalPog.planogram.bays.flatMap((bay) =>
          bay.shelves.flatMap((shelf) => shelf.items.map((item) => item.variant)),
        ),
      ),
    [originalPog],
  );
  const plain = useCallback(
    (text: string) => withoutCategory(text, categoryPrefix),
    [categoryPrefix],
  );
  /** A run of shelves or bays in words: "3", "2 to 7", "1, 3". */
  const rangeOf = (from: number, to: number) => t("placementRule.range", { from, to });
  /** A placement rule's question, its shelves in words and in the number they take. */
  const ruleTitleOf = (offer: { bandVariant: string; placement: RestrictionPlacement }) => {
    const shelves = offer.placement.cells.map((cell) => cell.shelf);
    return t(`placementRule.title_${pluralOf(new Set(shelves).size)}`, {
      variant: plain(offer.bandVariant),
      shelves: shelfSpanLabel(shelves, rangeOf),
    });
  };
  const flagTemplate = (key: string) => tDynamic(`flagWords.${key}`);
  /** The card's title, in its row and opened alike: a card that lists many blocks names a few
   *  and counts the rest. The flag's own title keeps every block, for the record a Keep writes. */
  const titleOf = (flag: Flag) => {
    if (isFlowCard(flag)) {
      return { title: t("resolver.flowCardTitle", { block: plain(flag.scopePath[0] ?? "") }) };
    }
    const fill = fillCardWordsOf(flag);
    if (fill) {
      return {
        title: t(`resolver.fillCardTitle_${pluralOf(fill.shelves.length)}`, {
          shelves: fill.shelves.join(", "),
        }),
      };
    }
    const words = flag.words ? phraseText(flag.words.title, flagTemplate) : flag.title;
    const short = shortFlagTitle(words, flag.scopePath);
    const pieces = piecesCardWordsOf(flag);
    const title = pieces
      ? t("resolver.piecesCardTitle", { block: pieces.block, count: pieces.pieces })
      : withBlocksCounted(
          short.title,
          listedBlocksOf(flag, short.context),
          flagTemplate("and"),
          (names, count) => t("resolver.fixAllMore", { names, count }),
        );
    return { title: plain(title), context: short.context && plain(short.context) };
  };
  /** A fix as Fix all's note names it, by its block, so the note keeps to one line. */
  const fixNameOf = (flag: Flag) => {
    if (isFlowCard(flag)) return titleOf(flag).title;
    const { block, gathered } = fixedBlockOf(flag);
    // A card on the whole plan names no block.
    if (block === "") return t("resolver.fixAllWholePlan");
    return gathered === null
      ? plain(block)
      : t("resolver.fixAllGathered", { block: plain(block), count: gathered });
  };
  const detailOf = (flag: Flag) => {
    if (isFlowCard(flag)) return t("resolver.flowCardDetail");
    const fill = fillCardWordsOf(flag);
    if (fill) return t(`resolver.fillCardDetail_${fill.reason ?? "fixable"}`);
    const pieces = piecesCardWordsOf(flag);
    return pieces
      ? t(`resolver.piecesCardDetail_${pluralOf(pieces.shelves.length)}`, {
          shelves: pieces.shelves.join(", "),
        })
      : plain(flag.words ? detailText(flag.words, flagTemplate) : flag.detail);
  };
  /** The card's "As built" and "Want" orders, in the reader's locale where the detector worded them. */
  const evidenceOf = (flag: Flag) => {
    const shown = flag.words?.evidence
      ? evidenceText(flag.words.evidence, flagTemplate)
      : flag.evidence;
    return {
      built: shown.built ? plain(shown.built.join(" → ")) : null,
      want: shown.want ? plain(shown.want.join(" → ")) : null,
    };
  };
  /** A flow card names no difference a placement rule could keep: it is fixed or dismissed. */
  const isFlowCard = (flag: Flag) => flag.id.startsWith(FLOW_CARD_PREFIX);
  /** The working plan as it stood before the first block was gathered: a gathered block
   *  becomes part of the working plan, and Reset fixes goes back to this. */
  const [planBeforeFixes, setPlanBeforeFixes] = useState<Pog | null>(resumedFixes.planBeforeFixes);
  /** A tree laid by a move, and the plan the move's other laying draws: a slide's replay by
   *  the generator, or the slide a replay was laid over (see `commitHierarchyMove`). */
  const [otherLaying, setOtherLaying] = useState<{ draft: StudioDraft; pog: Pog } | null>(null);
  /** A tree move is being weighed (see `reachOfMove`). */
  const [weighingMove, setWeighingMove] = useState(false);
  const [fixAllProgress, setFixAllProgress] = useState<{ step: number } | null>(null);
  const fixing = fixAllProgress !== null || weighingMove;
  /** Why a button waits while Fix all runs or a move is weighed: it would take no answer. */
  const fixingReason = fixAllProgress
    ? t("toolbar.waitForFixAll")
    : weighingMove
      ? t("toolbar.waitForMove")
      : undefined;
  const {
    inferenceOptions,
    aestheticsRules: profileAestheticsRules,
    hierarchyMoveRules,
    verticalBlockingOptions,
    semanticRoles,
  } = configuration;
  // The score says lengths in the category's unit; the profile's rules carry none.
  const aestheticsRules = useMemo(
    () => ({ ...profileAestheticsRules, lengthUnit }),
    [profileAestheticsRules, lengthUnit],
  );
  // The profile's section mappings, checked against the item master on the opened plan.
  const adapterOptions = useMemo(
    () => withLiveSectionSemantics([originalPog], configuration.adapterOptions),
    [originalPog, configuration.adapterOptions],
  );
  /** Questions answered No on this base planogram: kept past close, so they are not asked again.
   *  Detection is told too, so a declined offer explains nothing and its products stay strays. */
  const questionsKey = studioHistoryKey(basePogId);
  const [declinedQuestionIds, setDeclinedQuestionIds] = useState(() =>
    getDeclinedQuestions(questionsKey),
  );
  const session = useStudioSession(pogs, {
    adapterOptions,
    inferenceOptions,
    blockIdentityAttributes: semanticRoles.blockIdentity,
    quantityAttribute: semanticRoles.quantity,
    terminalVariantAttribute: semanticRoles.terminalVariant,
    brandAttribute: semanticRoles.brand,
    declinedSplitOffIds: declinedQuestionIds,
    declaredValues: configuration?.declaredValues,
    declaredLabels: configuration?.declaredLabels,
    basePogId,
    asBuiltPogs,
  });
  const { items, runs, draft, flags, asBuiltFlags, settledFlagIds, tree, layout } = session;
  /** A draft step is a new action: it voids the fixes Undo took back, as it voids the draft's
   *  own Redo. */
  const commit = (next: StudioDraft) => {
    if (next !== draft) setUndoneFixes([]);
    session.commit(next);
  };
  const { baseLayout } = session;
  const displayedBefore = session.undoDepth === 0 ? baseLayout : undefined;
  const renderedProjection = useMemo(() => renderedProjectionOf(pogs, items), [pogs, items]);
  // The opened plan's range and a draft's bands: what the Aesthetics score grades.
  const opened = useMemo(() => {
    try {
      return studioItemsOf([originalPog], adapterOptions);
    } catch {
      return null;
    }
  }, [originalPog, adapterOptions]);
  const scopeOf = useCallback(
    (graded: StudioDraft) => {
      if (!opened) return undefined;
      try {
        return aestheticsScopeOf(originalPog, opened.items, opened.shelfRestrictions, graded);
      } catch {
        return undefined;
      }
    },
    [originalPog, opened],
  );
  const scoreScope = useMemo(() => scopeOf(draft), [scopeOf, draft]);

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  /** A node moved `offset` places among its siblings, as the tree's buttons and drags move it. */
  const siblingStepOf = (node: StudioTreeNode, offset: number) =>
    applyHierarchyMove(
      items,
      runs,
      draft,
      siblingMoveOf(draft, node, offset),
      hierarchyMoveRules,
      displayedBefore,
      renderedProjection,
      aestheticsRules,
      verticalBlockingOptions.blockShare,
      scoreScope,
    );
  /** The selected node's step earlier or later, where the step would leave the shelf as it is:
   *  the button is disabled and says so, rather than offered and refused. */
  const selectedTreeNode = selectedNodeId ? tree.nodesById[selectedNodeId] : undefined;
  /** Found off the click, as the other offers are: until then both steps are offered. */
  const [siblingSteps, setSiblingSteps] = useState<{
    node: StudioTreeNode;
    draft: StudioDraft;
    earlier: string | null;
    later: string | null;
  } | null>(null);
  useEffect(() => {
    if (!selectedTreeNode || !isEditable) return;
    const timer = window.setTimeout(() => {
      const refusalOf = (offset: number) => {
        try {
          return siblingStepOf(selectedTreeNode, offset).report.committed
            ? null
            : t("hierarchy.stepChangesNothing");
        } catch {
          return null; // Offered: the step itself says why it is not made.
        }
      };
      setSiblingSteps({
        node: selectedTreeNode,
        draft,
        earlier: refusalOf(-1),
        later: refusalOf(1),
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [
    selectedTreeNode,
    isEditable,
    items,
    runs,
    draft,
    displayedBefore,
    renderedProjection,
    hierarchyMoveRules,
    aestheticsRules,
    verticalBlockingOptions,
    scoreScope,
    t,
  ]);
  const siblingStepRefusals =
    isEditable && siblingSteps?.node === selectedTreeNode && siblingSteps?.draft === draft
      ? siblingSteps
      : null;
  // Kept with the draft it produced: any other commit, undo or redo moves the draft on and the
  // report stops describing the plan.
  const [lastMove, setLastMove] = useState<{ report: MoveReport; draft: StudioDraft } | null>(null);
  const moveReport = lastMove && lastMove.draft === draft ? lastMove.report : null;
  const [selectedFlagId, setSelectedFlagId] = useAtom(cdtStudioSelectedFlagAtom);
  const [selectionSource, setSelectionSource] = useAtom(cdtStudioSelectionSourceAtom);
  const setPins = useSetAtom(cdtStudioPinsAtom);
  const setCards = useSetAtom(cdtStudioExceptionCardsAtom);
  const [actionRequest, setActionRequest] = useAtom(cdtStudioActionRequestAtom);
  const [previewOn, setPreviewOnState] = useState(
    // Always start OFF: the atom already holds whatever the user last kept.
    () => false,
  );
  const setPreviewOn = (value: boolean) => {
    setPreviewOnState(value);
    setPanelPreviewOn(pogAtom as object, value);
  };

  const inference = useInference(session.resolverInputs, lengthUnit);
  // The "Layout assumptions" list (inference exceptions) is withdrawn from the panel: the
  // merchant reads it as more violations. The exceptions still feed the resolver and tests.
  const shownLayoutAssumptions: InferenceException[] = [];
  const [selectedExceptionId, setSelectedExceptionId] = useState<string | null>(null);

  const poolingStatus = useMemo(
    () =>
      new Map(
        poolingQuestionOf(items, runs, draft).map((status) => [
          poolingKeyOf(status.attr, status.value),
          status,
        ]),
      ),
    [items, runs, draft],
  );

  const [blockingFixNote, setBlockingFixNote] = useState<string | null>(null);
  /** Fix all's last note: shown on one line, the whole of it on hover. */
  const [fixAllNote, setFixAllNote] = useState<string | null>(null);
  const noteIsFixAll = blockingFixNote !== null && blockingFixNote === fixAllNote;
  const [inPlaceRefusals, setInPlaceRefusals] = useState<Record<string, true>>({});
  /** Unanswered keeps Finalize disarmed; save failure cannot live on the closing modal. */
  const [gateOpen, setGateOpen] = useState(false);
  const [gateFailure, setGateFailure] = useState<string | null>(null);
  const gateGenerationRef = useRef(0);

  const [shipState, setShipState] = useState<
    | { status: "idle" }
    | { status: "shipping" }
    | {
        status: "shipped";
        text: string;
        /** The tree and the plan the CDTs were written from (see `finalLayout`). */
        draft: StudioDraft;
        working: Pog;
        planBeforeFixes: Pog | null;
        rulesUnsaved: boolean;
      }
    | { status: "refused"; text: string; unplaced: UnplacedCdtCombo[] }
  >({ status: "idle" });
  const [shipNotices, setShipNotices] = useState<readonly string[]>([]);
  /* DEV-3694: the category's products the base does not carry ship next to their closest match. */
  const storeRange = useMemo(
    () =>
      storeRangeProductsOf(
        configuration.adapterOptions.attributeOverlay,
        configuration.adapterOptions.nameOverlay,
        items,
      ),
    [configuration.adapterOptions, items],
  );
  const placeableStoreRange = useMemo(() => {
    const matches = closestBaseMatches(storeRange, items, draft);
    return storeRange.filter((product) => matches.get(product.productCode));
  }, [storeRange, items, draft]);
  const [storeRangeOpen, setStoreRangeOpen] = useState(false);
  const [placedStoreRange, setPlacedStoreRange] = useState<readonly PlacedStoreProduct[] | null>(
    null,
  );
  const [restrictionsRefusal, setRestrictionsRefusal] = useState<string | null>(null);
  const levelAttrForFlag = (flag: Flag): string | null => {
    const scope = flag.scopePath.join(PATH_SEPARATOR);
    const node = session.tree.nodesById[scope];
    const levelIndex = node?.branchLevels[0];
    if (levelIndex !== undefined) return draft.spine[levelIndex]?.attr ?? null;
    const roots = session.tree.rootIds.map((id) => session.tree.nodesById[id]?.depth ?? 0);
    const depth = scope === "" && roots.length > 0 ? Math.min(...roots) : flag.scopePath.length;
    return draft.spine[Math.min(depth, draft.spine.length - 1)]?.attr ?? null;
  };

  // Held in a ref so source changes cannot re-stamp; the first effect run must not rearrange the pog.
  const previewSourceRef = useRef({ layout, items, originalPog });
  previewSourceRef.current = { layout, items, originalPog };

  /** The pog this panel last stamped, so an external replacement is distinguishable from ours.
   *  Remounted over a standing preview, the plan on the atom is the one the studio stamped (the
   *  original's initializer leaves it to close), not an outside edit to take up. */
  const lastStampedPogRef = useRef<Pog | null>(
    getPanelPreviewState(pogAtom as object)?.previewOn && pog !== originalPog ? pog : null,
  );
  /** The plan on screen is an outside edit the studio took up and has not yet written over.
   *  Undo and redo step the draft and leave such a plan standing. */
  const outsideEditStandsRef = useRef(false);
  const authorOfPog = () =>
    outsideEditStandsRef.current
      ? "external"
      : pogAuthorOf(pog, originalPog, lastStampedPogRef.current);
  const [applyTick, setApplyTick] = useState(0);
  const applyPreview = () => {
    outsideEditStandsRef.current = false;
    setPreviewOn(true);
    setApplyTick((tick) => tick + 1);
  };

  const previewWasOn = useRef(previewOn);
  const previewMounted = useRef(false);
  useEffect(() => {
    if (!previewMounted.current) {
      previewMounted.current = true;
      previewWasOn.current = previewOn;
      return;
    }
    if (previewOn) {
      const source = previewSourceRef.current;
      const stamped = stampLayout(source.originalPog, source.layout, source.items);
      lastStampedPogRef.current = stamped;
      setPog(stamped);
      // The plan laid from the tree is the plan the session now works on; the fixes made on
      // the old plan are gone with it.
      setWorkingPog(stamped);
      setPlanBeforeFixes(null);
      setFixSteps([]);
    } else if (previewWasOn.current) {
      lastStampedPogRef.current = null;
      setPog(originalPog);
      setWorkingPog(originalPog);
      setPlanBeforeFixes(null);
      setFixSteps([]);
    }
    previewWasOn.current = previewOn;
  }, [previewOn, applyTick, setPog, originalPog]);

  // Someone else replaced the plan (Revert, page Undo, a drag on the canvas, an item-panel
  // edit). The plan on screen is the plan the studio works on: drop the preview and the
  // fixes, and take the new plan as the one every exception is measured
  // on and every fix starts from. Held to the old plan, a fix repaired shelves the user was
  // no longer looking at and stamped the result over their edit.
  useEffect(() => {
    // A plan written since this render (Undo right after a fix lands) is judged by its own
    // render: against the newer stamp, this one would read as someone else's.
    if (pogStore.get(pogAtom) !== pog) return;
    if (pogAuthorOf(pog, originalPog, lastStampedPogRef.current) !== "external") return;
    lastStampedPogRef.current = null;
    outsideEditStandsRef.current = true;
    setPreviewOn(false);
    setBlockingFixNote(null);
    setInPlaceRefusals({});
    const adopted = resetPanelPreviewState(pogAtom as object, pog).originalPog;
    setOriginalPog(adopted);
    setWorkingPog(adopted);
    setPlanBeforeFixes(null);
    setFixSteps([]);
    setUndoneFixes([]);
    setLastMove(null);
  }, [pog]);

  // Tab switches keep the preview; Close (Discard, or nothing to lose) restores the original
  // via the persisted flag, unless the plan on screen is the one just shipped.
  const closeStudio = () => {
    if (endPanelPreviewSession(pogAtom as object, previewOn) && !alreadyShipped) {
      setPog(originalPog);
      setPreviewOnState(false);
    }
    clearStudioHistory(studioHistoryKey(basePogId));
    onClose?.();
  };

  const shelfLength = runs.reduce((total, run) => total + run.widthCm * run.shelves.length, 0);

  /** `report` is the move's own, shown under the tree until the draft moves on; a draft laid
   *  without one (a tree edit, not a move) clears the last move's. */
  const applyMoveResult = (laid: StudioDraft, report: MoveReport | null = null) => {
    setBlockingFixNote(null);
    commit(laid);
    setLastMove(report ? { report, draft: laid } : null);
    applyPreview();
  };

  const commitHierarchyMove = (result: MoveResult) => {
    if (!result.report.committed) {
      setBlockingFixNote(t("resolver.treeEditNotMade"));
      return;
    }
    const layOf = ({ draft: moved, report }: { draft: StudioDraft; report: MoveReport }) => {
      const closed = closeFillWhereShort(
        originalPog,
        items,
        runs,
        moved,
        hierarchyMoveRules.fillFloorShare,
      );
      return { draft: closed.draft, report, verdict: closed.laid };
    };
    // A slide keeps the plan on screen; the generator's replay of the same move can draw the
    // better plan, or the one a slide would harm. Of those the rule lets through, the better
    // is laid, and the other laying is kept for Fix all to work too: either can stand in the
    // way of the better end.
    const standing = unfixedVerdict();
    const slid = layOf(result);
    const replayed = result.replay && layOf(result.replay);
    const allowed = (option: typeof slid) => !treeEditHarms(option.verdict, standing);
    const chosen =
      replayed &&
      allowed(replayed) &&
      (!allowed(slid) || improvesOn(replayed.verdict, slid.verdict))
        ? replayed
        : slid;
    const other = chosen === slid ? replayed : slid;
    layTreeEdit(
      chosen.draft,
      chosen.verdict,
      budgetQuestionOf(chosen.report, shelfLength),
      other?.draft ?? null,
      chosen.report,
    );
  };

  /**
   * A tree edit, laid as a move is: refused for harm, the note saying why (`layIfHarmless`);
   * else weighed for what Fix all then reaches (`reachOfMove`) and asked for what it costs
   * (`askCostThen`). `otherDraft` lays the move's other arrangement (see `commitHierarchyMove`).
   */
  const layTreeEdit = (
    next: StudioDraft,
    laidVerdict?: PlanVerdict,
    question: ReturnType<typeof budgetQuestionOf> = null,
    otherDraft: StudioDraft | null = null,
    report: MoveReport | null = null,
  ) =>
    layIfHarmless(
      next,
      (cost) => {
        const start = pog;
        const laid = stampLayout(originalPog, buildLayout(items, next, runs), items);
        const otherPog = otherDraft
          ? stampLayout(originalPog, buildLayout(items, otherDraft, runs), items)
          : null;
        setWeighingMove(true);
        void reachOfMove(next, laid, otherPog)
          .catch((error: unknown) => {
            // Unweighed, the move is asked as any tree edit is, and the note says so.
            console.error("Could not work out what Fix all reaches after the move.", error);
            return null;
          })
          .then((reach) => {
            // The plan was replaced while the move was weighed: the move belongs to a plan gone.
            if (pogRef.current !== start) return;
            askCostThen(
              cost,
              question,
              () => {
                applyMoveResult(next, report);
                setOtherLaying(otherPog ? { draft: next, pog: otherPog } : null);
                setBlockingFixNote(
                  reach
                    ? t("moveReport.reachNote", { after: reach.after })
                    : t("moveReport.reachUnknown"),
                );
              },
              reach,
            );
          })
          .finally(() => setWeighingMove(false));
      },
      laidVerdict,
    );

  /**
   * What Fix all reaches after the move, and without it, as the tile shows them; it holds Fix
   * all back where it leaves it any tile below. A tree edit lays the plan
   * again and discards the fixes, so it can raise the score and still leave a plan no fix gets
   * as far as the plan before it.
   * Without the move, Fix all starts from the plan from before the fixes, and the plan on screen
   * stands at what the fixes made of it. After it, Fix all works the plan laid and the move's
   * other laying, in the setting the session will then read the laid plan in.
   */
  const reachOfMove = async (moved: StudioDraft, laid: Pog, otherLaying: Pog | null) => {
    const laidItems = studioItemsOf([laid], adapterOptions).items;
    const settingAfter: FixSetting = {
      ...fixSetting,
      draft: moved,
      walkOrder: buildLayout(laidItems, moved, runs).walkOrder,
    };
    const [withMove, withoutMove] = await Promise.all([
      fixAllOnce(settingAfter, otherLaying ? [laid, otherLaying] : [laid]),
      fixAllOnce(fixSetting, [planBeforeFixes ?? workingPog]),
    ]);
    const before = Math.max(readingOf(pog).verdict.score, withoutMove.after.score);
    return {
      before: shownScore({ score: before }),
      after: shownScore(withMove.after),
      holdsBack: Math.round(withMove.after.score) < Math.round(before),
    };
  };

  /** One modal for what a tree edit costs, the fixes it discards, the score it loses and the
   *  facings or fill, asked before `proceed`; none when it costs nothing. What Fix all reaches
   *  is said whenever it was worked out, and asked where it holds Fix all back. */
  const askCostThen = (
    cost: ReturnType<typeof scoreCostOf>,
    question: ReturnType<typeof budgetQuestionOf>,
    proceed: () => void,
    reach: { before: number | string; after: number | string; holdsBack: boolean } | null = null,
    scoreTitle: "moveReport.scoreTitle" | "moveReport.previewScoreTitle" = "moveReport.scoreTitle",
  ) => {
    if (!cost && !question && !reach?.holdsBack && fixesMade === 0) {
      proceed();
      return;
    }
    const budget = !question
      ? null
      : question.kind === "facings"
        ? t("moveReport.budgetDescription", {
            facings: question.facingsLost,
            pct: question.pct,
            inches: question.inches,
          })
        : t("moveReport.budgetFillDescription", {
            pts: (question.fill ? question.fill.before - question.fill.after : 0).toFixed(1),
            before: question.fill?.before ?? 0,
            after: question.fill?.after ?? 0,
            inches: question.inches,
          });
    const discards =
      fixesMade > 0 && t(`moveReport.discardsFixes_${pluralOf(fixesMade)}`, { count: fixesMade });
    // "80 → 80" reads as nothing said: at the same shown score, the score stays.
    const scoreLine =
      cost &&
      (cost.scoreBefore === cost.scoreAfter
        ? t("moveReport.scoreStays", { score: cost.scoreAfter })
        : t("moveReport.scoreLine", cost));
    openConfirmModal({
      title: t(
        question
          ? question.kind === "facings"
            ? "moveReport.budgetTitle"
            : "moveReport.budgetFillTitle"
          : discards
            ? "moveReport.discardsTitle"
            : reach?.holdsBack
              ? "moveReport.reachTitle"
              : scoreTitle,
      ),
      description: [
        discards,
        scoreLine,
        reach && t("moveReport.reachLine", { before: reach.before, after: reach.after }),
        budget,
      ]
        .filter(Boolean)
        .join(" "),
      icon: { name: "alert", color: Color.yellow },
      buttons: [
        {
          id: "cancel",
          label: t("moveReport.budgetCancel"),
          color: "primary",
          variant: "inverted",
        },
        {
          id: "proceed",
          label: t("moveReport.budgetProceed"),
          color: "yellow",
          onClick: proceed,
        },
      ],
    });
  };

  const sortedFlags = useMemo(
    () =>
      [...flags].sort(
        (a, b) => severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity),
      ),
    [flags],
  );

  // Open offers read the base as it stands with the kept carves undone (`offerBaselineOf`).
  const baselineDraft = useMemo(() => offerBaselineOf(draft), [draft]);
  const restrictionOffers = useMemo(
    () => buildRestrictionOffers(items, runs, baselineDraft, "fence", lengthUnit),
    [items, runs, baselineDraft, lengthUnit],
  );
  const itemsBySku = useMemo(() => new Map(items.map((item) => [item.skuId, item])), [items]);

  const keptOffers = draft.keptPlacementOffers ?? [];
  const displayOffers = useMemo(() => {
    const keptIds = new Set(
      keptOffers.map((offer) => placementRuleIdOf(offer.bandVariant, offer.placement)),
    );
    return [
      ...keptOffers,
      ...restrictionOffers.filter(
        (offer) => !keptIds.has(placementRuleIdOf(offer.bandVariant, offer.placement)),
      ),
    ];
  }, [keptOffers, restrictionOffers]);

  const ruleDecisionOf = (offer: (typeof restrictionOffers)[number]) =>
    draft.placementRuleDecisions?.[placementRuleIdOf(offer.bandVariant, offer.placement)];

  const keptDisplayOffers = useMemo(
    () =>
      displayOffers.filter(
        (offer) =>
          draft.placementRuleDecisions?.[placementRuleIdOf(offer.bandVariant, offer.placement)] ===
          "keep",
      ),
    [displayOffers, draft.placementRuleDecisions],
  );
  const openDisplayOffers = useMemo(
    () => displayOffers.filter((offer) => !keptDisplayOffers.includes(offer)),
    [displayOffers, keptDisplayOffers],
  );
  const selectedPlacementOffer = useMemo(
    () =>
      selectedFlagId
        ? displayOffers.find(
            (offer) => placementRuleIdOf(offer.bandVariant, offer.placement) === selectedFlagId,
          )
        : undefined,
    [displayOffers, selectedFlagId],
  );
  const selectedException = selectedExceptionId
    ? inference.exceptions.find((exception) => exception.id === selectedExceptionId)
    : undefined;
  useEffect(
    () => () => setHighlights((current) => (current.length === 0 ? current : [])),
    [setHighlights],
  );
  /** The header's More menu: Redo, the fixes, Save to file and the preview. */
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => {
    if (!moreOpen) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [moreOpen]);
  /** Questions answered Yes in this panel, for their one-line "is now its own brand · Undo".
   *  `draftAfter` is the draft the Yes committed: the editing history keeps drafts by
   *  reference, so the Yes is the latest step exactly while it is the present draft. */
  const [answeredQuestions, setAnsweredQuestions] = useState<
    readonly {
      id: string;
      /** The card whose number the answer keeps: its own, or the size tags' line. */
      numberId: string;
      flagIds: readonly string[];
      text: string;
      draftAfter: StudioDraft;
    }[]
  >([]);
  const [tidyUpsOpen, setTidyUpsOpen] = useState(false);
  /** The draft with one placement rule decided (or its answer withdrawn, `undefined`). */
  const ruleDecidedDraft = (
    current: StudioDraft,
    offer: (typeof restrictionOffers)[number],
    decision: "keep" | "decline" | undefined,
  ): StudioDraft => {
    const key = placementRuleIdOf(offer.bandVariant, offer.placement);
    const next: Record<string, "keep" | "decline"> = {
      ...(current.placementRuleDecisions ?? {}),
    };
    if (decision === undefined) delete next[key];
    else next[key] = decision;
    const others = (current.keptPlacementOffers ?? []).filter(
      (kept) => placementRuleIdOf(kept.bandVariant, kept.placement) !== key,
    );
    let draftNext: StudioDraft = { ...current, placementRuleDecisions: next };
    if (decision === "keep") {
      // The offer was found with the kept carves undone; its carve goes on with the order the
      // draft has NOW, so earlier keeps and the merchant's own root order stay.
      if (offer.promotion)
        draftNext = adoptPromotion(
          draftNext,
          promotionOn(items, draftNext, runs, offer.promotion),
          items,
        );
      draftNext = { ...draftNext, keptPlacementOffers: [...others, offer] };
    } else {
      if (offer.promotion) draftNext = revertPromotion(draftNext, offer.bandVariant);
      draftNext = { ...draftNext, keptPlacementOffers: others };
    }
    return draftNext;
  };
  const decideRule = (
    offer: (typeof restrictionOffers)[number],
    decision: "keep" | "decline" | undefined,
  ) => commit(ruleDecidedDraft(draft, offer, decision));
  /** Several rules decided in panel order, as one step the toolbar's Undo takes back. */
  const decideRules = (
    choices: readonly {
      offer: (typeof restrictionOffers)[number];
      decision: "keep" | "decline" | undefined;
    }[],
  ) =>
    commit(
      choices.reduce(
        (current, { offer, decision }) => ruleDecidedDraft(current, offer, decision),
        draft,
      ),
    );

  const gateRefusalCopy = (refusal: string): string => {
    if (refusal === "no rows to add") return t("panel.gateRefusalNoRows");
    if (refusal.startsWith("no category id")) return t("panel.gateRefusalNoCategory");
    if (refusal.includes("have no store codes")) return t("panel.gateRefusalEmptyStoreScope");
    if (refusal.startsWith("cannot append")) return t("panel.gateRefusalCannotRead");
    return t("panel.gateRefusalGeneric", { detail: refusal });
  };

  const openGate = async (
    offersToWrite: readonly (typeof restrictionOffers)[number][],
    shippedRelease: ShippedRelease,
  ) => {
    if (offersToWrite.length === 0) return null;
    const owns = gateOwnershipLinesOf(offersToWrite, rangeOf);
    const generation = ++gateGenerationRef.current;
    // Set before the read so a second Finalize cannot slip in behind the question.
    setGateOpen(true);
    setGateFailure(null);

    // Both write paths replace the category, so the old set is never consulted.
    const existing =
      categoryId !== null
        ? { kind: "replaceCategory" as const }
        : {
            kind: "unavailable" as const,
            reason: "missingCategoryId",
          };

    if (generation !== gateGenerationRef.current) return null;

    const contributions: RestrictionContribution[] = offersToWrite.map((offer) => ({
      rows: offer.rows,
      cost: offer.cost,
      conflictInputs: {
        // The scan runs against the FENCE; the band's own cells go in as `keptCells` and are
        // used for the mirror-exclusion check alone.
        placement: offer.fence ?? offer.placement,
        keptCells: offer.placement,
        bandVariant: offer.bandVariant,
        shippedVariants: offer.shippedVariants,
        fixture: offer.fixture,
      },
    }));
    const proposal = proposeRestrictionsForAll(categoryId ?? "", contributions, existing);
    if (proposal.deletions.length > 0) {
      console.debug("[cdt-studio] restriction gate deletions", proposal.deletions);
    }
    if (proposal.supersededGroups.length > 0) {
      console.debug(
        "[cdt-studio] restriction gate supersedes studio-owned groups",
        proposal.supersededGroups,
      );
    }
    console.debug(
      "[cdt-studio] restriction gate writes",
      proposal.payload?.groups.map((group) => ({
        id: group.variant_group_id,
        rules: group.rules.length,
      })) ?? [],
    );
    console.debug("[cdt-studio] restriction gate rows", proposal.rows);

    const description = (
      <Flex column gap="6px" data-testid="cdt-studio-gate-modal">
        {owns.map((line) => (
          <Text
            key={`${line.variant}-${line.shelves}`}
            textAlign="center"
            variant="small2"
            color={Color.textSecondary}
          >
            {t(line.shelfCount === 1 ? "panel.gateOwnsShelf" : "panel.gateOwnsShelves", {
              variant: plain(line.variant),
              shelves: line.shelves,
            })}
          </Text>
        ))}
        <Text textAlign="center" variant="small2" color={Color.textSecondary}>
          {proposal.cost.evictions === 0
            ? t("panel.gateNoCost")
            : t("panel.gateEvicts", {
                evictions: proposal.cost.evictions,
                siblings: proposal.cost.siblingVariants,
              })}
        </Text>
        {proposal.carriedThrough.length > 0 && (
          <Text textAlign="center" variant="small2" color={Color.textSecondary}>
            {t("panel.gateCarried", { count: proposal.carriedThrough.length })}
          </Text>
        )}
        {proposal.refusals.map((refusal) => (
          <Text key={refusal} textAlign="center" variant="small2" color={Color.alertHighText}>
            {gateRefusalCopy(refusal)}
          </Text>
        ))}
      </Flex>
    );

    const canSave = proposal.payload !== null && proposal.payload !== undefined;
    openConfirmModal({
      title: t("panel.gateTitle"),
      description,
      buttons: canSave
        ? [
            { id: "cdt-studio-gate-cancel", label: t("panel.gateCancel"), onClick: cancelGate },
            {
              id: "cdt-studio-gate-ok",
              label: t("panel.gateOk"),
              color: "success",
              // Close the gate first; a later ModalConfirm close would dismiss the generation step.
              onClick: async () => {
                const closeResult = await closeModal("ModalConfirm");
                if (closeResult !== "closed") {
                  return false;
                }
                await agreeGate(proposal, shippedRelease);
                return false;
              },
            },
          ]
        : [{ id: "cdt-studio-gate-cancel", label: t("panel.gateCancel"), onClick: cancelGate }],
    });
    return proposal;
  };

  const agreeGate = async (proposal: RestrictionProposal, shippedRelease: ShippedRelease) => {
    const consent = agreeToRestrictions(proposal);
    if (!consent) return;
    const outcome = await applyRestrictionsThenGenerate(
      consent,
      saveVariantRestrictions,
      async () => (onGenerate ? onGenerate(shippedRelease) : null),
    );
    setGateOpen(false);
    // The save receipt is console-only (owner, 2026-09-02). A FAILURE is not: it lands on
    // the panel, because by the time it is known the modal that asked is already closing.
    if (outcome.committed.ok) {
      console.debug("[cdt-studio] restrictions saved", outcome.committed.message);
    } else {
      setGateFailure(outcome.committed.error);
    }
  };

  /** Closing the gate after ship leaves a release without its rules; the banner must say so. */
  const cancelGate = () => {
    gateGenerationRef.current += 1;
    declineRestrictions();
    setGateOpen(false);
    setShipState((current) =>
      current.status === "shipped" ? { ...current, rulesUnsaved: true } : current,
    );
    // The plan shipped stays: the next open takes it as its original.
    endPanelPreviewSession(pogAtom as object, previewOn);
    onClose?.();
  };

  /** The arrangement the CDTs are written from: the plan on screen once fixes stand on it (the
   *  working plan is then the fixed one), else the plan the tree lays. */
  const finalLayout = planBeforeFixes !== null ? baseLayout : layout;

  const alreadyShipped =
    shipState.status === "shipped" &&
    shipState.draft === draft &&
    shipState.working === workingPog &&
    shipState.planBeforeFixes === planBeforeFixes;
  /** Dirty means something would be LOST by closing; anything that survives the close is not,
   *  and nothing is lost of a plan just shipped. */
  const isDirty =
    !alreadyShipped &&
    studioIsDirty({
      undoDepth: session.undoDepth,
      fixesAdopted: planBeforeFixes !== null,
      draft,
    });
  const hasFinalizableTree = items.length > 0 && draft.spine.length > 0;

  const downloadCsvs = () => {
    const result = finalizeCdts(
      items,
      withProfileLevels(draft, configuration.profileLevels),
      runs,
      finalLayout,
    );
    setRestrictionsRefusal(null);
    downloadFile({
      name: "cdt-assignments",
      extension: "csv",
      data: toCdtAssignmentsCsv(result),
    });
    if (result.restrictions.length === 0) return;
    const shippedVariants = [
      ...new Set(
        Object.values(result.contract.itemValues)
          .map((values) => values[0] ?? "")
          .filter((value) => value !== ""),
      ),
    ].sort();
    const file = toVariantRestrictionsFileCsv(
      restrictionsToFileRows(result.restrictions),
      shippedVariants,
      storeCodes,
    );
    if (file.ok) {
      downloadFile({ name: "variant-restrictions", extension: "csv", data: file.csv });
    } else {
      setRestrictionsRefusal(file.refusal);
    }
    downloadFile({
      name: "variant-restrictions-detail",
      extension: "csv",
      data: toRestrictionsDetailCsv(result.restrictions),
    });
  };

  const onFinalize = async (acknowledgeUnplacedCombos = false) => {
    if (finalizeBlocked) return;
    const gatedOffer = displayOffers.find((offer) => ruleDecisionOf(offer) === undefined);
    const offersToWrite =
      keptDisplayOffers.length > 0 ? keptDisplayOffers : gatedOffer ? [gatedOffer] : [];
    const offeredDraft = offersToWrite.reduce(
      (current, offer) =>
        offer.promotion ? applyPromotion(current, offer.promotion, items) : current,
      draft,
    );
    let shipDraft: StudioDraft;
    try {
      shipDraft = shipDraftOf(items, offeredDraft, runs, configuration.profileLevels);
    } catch (error) {
      console.error("Could not adopt the measured store bands.", error);
      setShipState({ status: "refused", text: t("panel.storeRulesRequired"), unplaced: [] });
      return;
    }
    const result = finalizeCdts(items, shipDraft, runs, finalLayout);

    const notices: string[] = [];
    if (!result.verification.orderPreserved) notices.push(t("panel.orderChanged"));
    if (result.staleRestrictions.length > 0) {
      notices.push(
        t("panel.staleRules", {
          count: result.staleRestrictions.length,
          rules: plain(staleRuleNamesOf(result.staleRestrictions)),
        }),
      );
    }
    setShipNotices(notices);

    if (merchflowId === undefined) {
      setShipState({ status: "refused", text: t("panel.shipNoMerchflow"), unplaced: [] });
      return;
    }

    setShipState({ status: "shipping" });

    // Child planograms use this measurement, so it must match the Studio tree.
    let payload: ReturnType<typeof prepareCompressionPayloadWithCoverage>["payload"];
    try {
      const prepared = prepareCompressionPayloadWithCoverage({
        result,
        draft: shipDraft,
        items,
        runs,
        basePogId,
        storeRange,
        lengthUnit,
      });
      payload = prepared.payload;
      setPlacedStoreRange(prepared.coverage.placed);
    } catch (error) {
      console.error("Could not prepare the store rule set for this CDT.", error);
      setShipState({
        status: "refused",
        text: t("panel.storeRulesRequired"),
        unplaced: [],
      });
      return;
    }

    let shipped;
    try {
      shipped = await shipCdtCompression(merchflowId, payload, acknowledgeUnplacedCombos);
    } catch (error) {
      setShipState({
        status: "refused",
        text: describeShipFailure(error),
        unplaced: unplacedCombosOf(error),
      });
      return;
    }

    const shippedRelease: ShippedRelease = {
      cdtReleaseId: shipped.releaseId,
      wallBlocking: payload.ruleSet.wallBlocking ?? "vertical",
    };
    // Store generation reads the release by base planogram: without one, the
    // page would not follow this release, so it is not promised either.
    if (typeof basePogId === "number") {
      setLatestRelease({
        basePogId,
        releaseId: shippedRelease.cdtReleaseId,
        wallBlocking: shippedRelease.wallBlocking,
      });
      toast({
        title: t("panel.shippedBlockingTitle"),
        message: t(
          shippedRelease.wallBlocking === "horizontal"
            ? "panel.shippedBlockingHorizontal"
            : "panel.shippedBlocking",
          { releaseId: shipped.releaseId },
        ),
      });
    }

    const shippedUnplaced = shipped.unplacedCombos ?? [];
    setShipState({
      status: "shipped",
      draft,
      working: workingPog,
      planBeforeFixes,
      rulesUnsaved: false,
      text:
        shippedUnplaced.length > 0
          ? t("panel.shippedWithGaps", {
              count: shipped.itemCount,
              treeId: shipped.treeId,
              releaseId: shipped.releaseId,
              dropped: unplacedProductCodesOf(shippedUnplaced).length,
            })
          : t("panel.shipped", {
              count: shipped.itemCount,
              treeId: shipped.treeId,
              releaseId: shipped.releaseId,
            }),
    });

    if (offersToWrite.length > 0) {
      await openGate(offersToWrite, shippedRelease);
      return;
    }
    // The plan shipped stays: the next open takes it as its original.
    endPanelPreviewSession(pogAtom as object, previewOn);
    onClose?.();
    // Stores should now be generated from the CDT just shipped, so that is the next step.
    await onGenerate?.(shippedRelease);
  };

  const acceptVariantFor = (flag: Flag) =>
    cdt0ResolutionOf(items, draft, runs).variantFor(flag.skuIds);

  const acceptFlagHere = (flag: Flag) =>
    acceptFlag(draft, flag, cdt0ResolutionOf(items, draft, runs));

  const isFlagResolved = (flag: Flag): boolean => settledFlagIds.has(flag.id);

  const raisedFindings = useMemo(
    () => sortedFlags.filter((flag) => !settledFlagIds.has(flag.id)),
    [sortedFlags, settledFlagIds],
  );

  /** The tree as the fixes read it, and the plan it lays. A decision on a card (dismissed,
   *  accepted, its tree change recorded) leaves the tree as it was, so the fixes found on the
   *  plan, the offers and every opened card's fix stand. */
  const fixDraftRef = useRef(draft);
  const fixDraft = useMemo(() => {
    if (!sameTreeForFixes(fixDraftRef.current, draft)) fixDraftRef.current = draft;
    return fixDraftRef.current;
  }, [draft]);
  /** The layout that tree lays, taken when it, the products or the runs change. */
  const fixLayoutRef = useRef({ fixDraft, items, runs, layout });
  const fixLayout = useMemo(() => {
    const last = fixLayoutRef.current;
    if (last.fixDraft !== fixDraft || last.items !== items || last.runs !== runs) {
      fixLayoutRef.current = { fixDraft, items, runs, layout };
    }
    return fixLayoutRef.current.layout;
  }, [fixDraft, items, runs, layout]);

  /** What every fix is made with: the plan the studio opened on, the tree, and the cards as
   *  the session raises them, so a fix sees the list the panel shows next. */
  // On a store plan, the minimum days of supply its request sent hold every cut (as Autofix).
  const { grading: storePogGrading } = useStorePogGrading();
  const sentMinDays = storePogGrading?.sent?.minDays ?? null;
  const fixSetting = useMemo<FixSetting>(
    () => ({
      basePog: originalPog,
      runs,
      draft: fixDraft,
      adapterOptions,
      detection: {
        quantityAttribute: semanticRoles.quantity,
        crossLevelNameFlags: false,
        blockIdentityAttributes: semanticRoles.blockIdentity,
        verticalBlockShare: inferenceOptions?.verticalBlockShare,
        terminalVariantAttribute: semanticRoles.terminalVariant,
        brandAttribute: semanticRoles.brand,
        declinedSplitOffIds: declinedQuestionIds,
        declaredValues: configuration.declaredValues,
        declaredLabels: configuration.declaredLabels,
      },
      walkOrder: fixLayout.walkOrder,
      vertical: {
        minFillRatio: verticalBlockingOptions.minFillRatio,
        preserveRange: verticalBlockingOptions.preserveRange,
        onlyIfBetter: verticalBlockingOptions.onlyIfBetter,
        rules: aestheticsRules,
        verticalBlockShare: verticalBlockingOptions.blockShare,
        // Graded on the fixes' tree, so a decision on a card leaves the fixes found standing.
        scope: scopeOf(fixDraft),
      },
      sentMinDays,
    }),
    [
      sentMinDays,
      originalPog,
      runs,
      fixDraft,
      adapterOptions,
      semanticRoles,
      inferenceOptions,
      configuration,
      fixLayout,
      verticalBlockingOptions,
      aestheticsRules,
      scopeOf,
      declinedQuestionIds,
    ],
  );
  const readingOf = (plan: Pog): PlanReading => readPlan(fixSetting, plan);
  /** Each card's fix on the plan on screen, found when the card is opened: a card offers
   *  Auto-fix only when it has one, and its tree change (`drafts`) only when that holds. */
  const [cardFixes, setCardFixes] = useState<{
    plan: Pog;
    setting: FixSetting;
    byFlag: Readonly<Record<string, PlanFix | null>>;
    drafts: Readonly<Record<string, StudioDraft | null>>;
  } | null>(null);
  /** Fix vertical blocking, and whether the plan built from the tree holds, found for each
   *  plan on screen. */
  const [offers, setOffers] = useState<{
    plan: Pog;
    setting: FixSetting;
    layout: GeneratedLayout;
    vertical: { fix: PlanFix | null; note: string };
    standing: PlanVerdict;
    /** The plan from before the fixes, and its verdict (see `unfixedVerdict`). */
    unfixed: Pog;
    judged: PlanVerdict;
    /** The plan the tree lays. */
    laid: PlanVerdict;
    /** That plan puts every product where the plan on screen does. */
    laidAsShown: boolean;
  } | null>(null);
  const shownOffers =
    offers?.plan === pog &&
    offers.setting === fixSetting &&
    offers.layout === fixLayout &&
    offers.unfixed === (planBeforeFixes ?? workingPog)
      ? offers
      : null;
  /** Once fixes are made, Fix vertical blocking can square the plan from before them and fix
   *  the cards again on it (see `squaredBeforeFixesOf`), found off the main thread. */
  const [squaredBefore, setSquaredBefore] = useState<{
    plan: Pog;
    setting: FixSetting;
    unfixed: Pog;
    fix: PlanFix | null;
  } | null>(null);
  const shownSquaredBefore =
    squaredBefore?.plan === pog &&
    squaredBefore.setting === fixSetting &&
    squaredBefore.unfixed === planBeforeFixes
      ? squaredBefore.fix
      : null;
  const shownVertical =
    shownSquaredBefore &&
    shownOffers &&
    !(
      shownOffers.vertical.fix &&
      shownOffers.vertical.fix.verdict.exactScore >= shownSquaredBefore.verdict.exactScore
    )
      ? {
          fix: shownSquaredBefore,
          note: t("resolver.blockingFixedBeforeFixes", {
            scoreBefore: shownScore(shownOffers.standing),
            scoreAfter: shownScore(shownSquaredBefore.verdict),
          }),
        }
      : shownOffers?.vertical;
  /** Laying the plan from the tree is a tree edit: refused for harm, asked for a lower score
   *  or for the fixes it discards, and refused where it would change nothing on screen. Turning
   *  the preview off goes back to the original plan, as Reset fixes does. */
  const previewRefusal =
    previewOn || !shownOffers
      ? null
      : treeEditHarms(shownOffers.laid, shownOffers.judged)
        ? t(previewRefusalKey(shownOffers.laid))
        : shownOffers.laidAsShown
          ? t("panel.previewMatches")
          : null;
  /** Turned by the user, the preview is a new action: it voids the fixes Undo took back. */
  const turnPreview = (on: boolean) => {
    const turn = () => {
      setUndoneFixes([]);
      setPreviewOn(on);
    };
    if (on && shownOffers) {
      askCostThen(
        treeEditCostOf(shownOffers.laid, shownOffers.standing, shownOffers.judged),
        null,
        turn,
        null,
        "moveReport.previewScoreTitle",
      );
    } else turn();
  };

  /** The cards raised from the tile (blocks in pieces no card names, blocks out of flow), with
   *  their fixes, found for each plan on screen. */
  const [raisedCards, setRaisedCards] = useState<{
    plan: Pog;
    setting: FixSetting;
    cards: readonly RaisedCard[];
  } | null>(null);
  // An answer can change the tree the fixes read without moving a product: the cards raised on
  // this plan stay on screen while they are worked out again, rather than vanish for a moment
  // (Dana, 2026-10-30: the amber line went after Confirm). Their fixes count only once found
  // for the tree as it is now (`raisedCardFixes`).
  const shownRaisedCards = useMemo(
    () =>
      raisedCards?.plan === pog
        ? raisedCards.cards.filter((card) => !isFlagSettled(draft, card.flag.id))
        : [],
    [raisedCards, pog, draft],
  );
  const raisedCardFixes = useMemo(
    () =>
      raisedCards?.setting === fixSetting
        ? Object.fromEntries(shownRaisedCards.map((card) => [card.flag.id, card.fix]))
        : {},
    [raisedCards, fixSetting, shownRaisedCards],
  );
  // One row per exception: the detector can raise the same finding twice.
  const detectedFindings = useMemo(
    () =>
      [...raisedFindings, ...shownRaisedCards.map((card) => card.flag)].filter(
        (flag, at, all) => all.findIndex((other) => other.id === flag.id) === at,
      ),
    [raisedFindings, shownRaisedCards],
  );
  const openFindings = useMemo(
    () =>
      declinedQuestionIds.size === 0
        ? detectedFindings
        : detectedFindings.filter((flag) => !declinedQuestionIds.has(flag.id)),
    [detectedFindings, declinedQuestionIds],
  );

  const brandAttr = semanticRoles.brand;
  /**
   * The questions are decisions about the base planogram as merchandised, so they are asked from
   * the as-built detection whatever the panel displays: Yes turns the regenerated preview on, and
   * the regenerated layout may no longer raise another question's offer. The session works on
   * that layout once it is stamped, so the as-built detection is its own (`asBuiltFlags`). An
   * offer only the displayed list raises is asked too, after them: the tidy-up line never holds
   * an offer, so otherwise it would show nowhere. An applied question is gone from here; a
   * declined one is hidden behind one quiet line.
   */
  const questionOffers = useMemo(() => {
    const open = (flag: Flag) => isQuestionFlag(flag, brandAttr) && !isFlagSettled(draft, flag.id);
    const asBuilt = asBuiltFlags.filter(open);
    const asBuiltIds = new Set(asBuilt.map((flag) => flag.id));
    const displayedOnly = detectedFindings.filter((flag) => open(flag) && !asBuiltIds.has(flag.id));
    return [...asBuilt, ...displayedOnly];
  }, [asBuiltFlags, detectedFindings, brandAttr, draft]);
  /** A brand block the base lays out neither way ships only the merchant's answer: Finalize
   *  waits for every size layout question the cards ask (Wendy, 2026-10-23). */
  const unansweredLayouts = unansweredSizeLayoutFlags(questionOffers, draft);
  const { questions } = useMemo(
    () =>
      groupStudioQuestions(
        questionOffers.filter((flag) => !declinedQuestionIds.has(flag.id)),
        { brandAttr, brandOf: () => null },
      ),
    [questionOffers, declinedQuestionIds, brandAttr],
  );
  const { questions: hiddenQuestions } = useMemo(
    () =>
      groupStudioQuestions(
        questionOffers.filter((flag) => declinedQuestionIds.has(flag.id)),
        { brandAttr, brandOf: () => null },
      ),
    [questionOffers, declinedQuestionIds, brandAttr],
  );
  // The tidy-ups follow the list the panel displays.
  const { tidyUps } = useMemo(
    () =>
      groupStudioQuestions(openFindings, {
        brandAttr,
        brandOf: (flag) => {
          if (!brandAttr) return null;
          const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, flag.scopePath);
          const index = levels.findIndex((level) => level.attr === brandAttr);
          return index >= 0 ? flag.scopePath[index] || null : null;
        },
      }),
    [openFindings, brandAttr, draft.spine, draft.scopedLevelOrder],
  );
  /**
   * What the merchant decides, in the order the panel asks it: each brand's layout, each split,
   * the shelf bands as one card, and the size tags under one line. Finalize waits for every one
   * (Dana, 2026-10-30); the bands count as answered once "Keep these N" decided each of them.
   */
  const layoutQuestions = questions.filter((question) => question.flags[0].question.sizeLayout);
  const sizeTagQuestions = questions.filter(
    (question) => !question.flags[0].question.sizeLayout && question.flags[0].question.sizeTier,
  );
  const splitQuestions = questions.filter(
    (question) => !question.flags[0].question.sizeLayout && !question.flags[0].question.sizeTier,
  );
  const undecidedOffers = openDisplayOffers.filter((offer) => ruleDecisionOf(offer) === undefined);
  const openAnswers: readonly string[] = [
    ...layoutQuestions.map((question) =>
      t("footer.layoutOf", { brand: question.flags[0].question.brand }),
    ),
    ...splitQuestions.map((question) =>
      t("footer.splitOf", {
        name: question.flags
          .map((flag) => flag.question.newBrand)
          .join(t("panel.questions.listAnd")),
      }),
    ),
    ...(undecidedOffers.length > 0
      ? [t(`footer.bands_${pluralOf(undecidedOffers.length)}`, { count: undecidedOffers.length })]
      : []),
    ...(sizeTagQuestions.length > 0
      ? [
          t(`footer.sizeTags_${pluralOf(sizeTagQuestions.length)}`, {
            count: sizeTagQuestions.length,
          }),
        ]
      : []),
  ];
  const finalizeBlocked =
    !hasFinalizableTree ||
    shipState.status === "shipping" ||
    gateOpen ||
    alreadyShipped ||
    fixing ||
    unansweredLayouts.length > 0 ||
    openAnswers.length > 0;
  const finalizeWaitReason =
    fixingReason ??
    (unansweredLayouts.length > 0
      ? t("panel.questions.sizeLayoutFirst", {
          brand: unansweredLayouts[0].question?.brand ?? "",
        })
      : openAnswers.length > 0
        ? t(`footer.waiting_${pluralOf(openAnswers.length)}`, { count: openAnswers.length })
        : undefined);
  /** The tree folds to one line while anything is left to decide, unless the merchant opens it;
   *  once nothing is, it stands open again. */
  const [treeChoice, setTreeChoice] = useState<"open" | "folded" | null>(null);
  const nothingToDecide = openAnswers.length === 0;
  useEffect(() => {
    if (nothingToDecide) setTreeChoice(null);
  }, [nothingToDecide]);
  const treeFolded = treeChoice === null ? !nothingToDecide : treeChoice === "folded";
  /** Cards folded by their chevron, by question id ("bands" for the shelf bands card). */
  const [foldedCards, setFoldedCards] = useState<ReadonlySet<string>>(() => new Set());
  const toggleCard = (id: string) =>
    setFoldedCards((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const [sizeTagsOpen, setSizeTagsOpen] = useState(false);
  /** A layout picked on a card and not yet confirmed: none is picked for the merchant. */
  const [layoutPicks, setLayoutPicks] = useState<Readonly<Record<string, SizeLayout>>>({});
  /** Unticked shelf bands, by rule id: every band starts ticked. */
  const [untickedBands, setUntickedBands] = useState<ReadonlySet<string>>(() => new Set());

  const answeredLines = answeredQuestions.filter((answer) =>
    answer.flagIds.every((flagId) => draft.appliedFixFlagIds.includes(flagId)),
  );

  useEffect(() => {
    // A question is asked from the as-built detection, so its products come from there even
    // while the preview shows a layout that no longer raises it.
    const questionFlagIds = selectedFlagId ? flagIdsOfQuestionId(selectedFlagId) : [];
    const asBuiltQuestion = asBuiltFlags.filter(
      (flag) => questionFlagIds.includes(flag.id) && isQuestionFlag(flag, brandAttr),
    );
    const questionSkuIds = (
      asBuiltQuestion.length > 0
        ? asBuiltQuestion
        : detectedFindings.filter(
            (flag) => questionFlagIds.includes(flag.id) && isQuestionFlag(flag, brandAttr),
          )
    ).flatMap((flag) => flag.skuIds);
    const skuIds = selectedFlagId
      ? questionSkuIds.length > 0
        ? questionSkuIds
        : (openFindings.find((flag) => flag.id === selectedFlagId)?.skuIds ??
          selectedPlacementOffer?.skuIds ??
          [])
      : selectedException
        ? selectedException.skuIds.length > 0
          ? selectedException.skuIds
          : selectedException.scopeKey
            ? session.skuIdsUnderNode(selectedException.scopeKey)
            : []
        : selectedNodeId
          ? session.skuIdsUnderNode(selectedNodeId)
          : [];
    const uniqueIds = canvasIdsOfSkus(pog, new Set(skuIds));
    setHighlights((current) => (sameHighlightIds(current, uniqueIds) ? current : uniqueIds));
  }, [
    selectedFlagId,
    selectedNodeId,
    selectedException,
    selectedPlacementOffer,
    openFindings,
    detectedFindings,
    asBuiltFlags,
    brandAttr,
    pog,
    setHighlights,
  ]);
  const cardFixesShown = cardFixes?.plan === pog && cardFixes.setting === fixSetting;
  const knownFixes = useMemo<Readonly<Record<string, PlanFix | null>>>(
    () => ({
      ...(cardFixesShown ? cardFixes.byFlag : {}),
      ...raisedCardFixes,
    }),
    [cardFixes, cardFixesShown, raisedCardFixes],
  );
  const knownDrafts = cardFixesShown ? cardFixes.drafts : {};
  /** A card with no fix: refused in place, or none found and no tree change offered. Its row
   *  and its pin say so, and why. */
  const cannotFixOf = (flag: Flag) =>
    inPlaceRefusals[flag.id] === true ||
    (flag.id in knownFixes && knownFixes[flag.id] === null && !knownDrafts[flag.id]);
  const cannotFixReason = t(
    shownOffers && Number.isNaN(shownOffers.standing.score)
      ? "resolver.cannotRepairInPlaceDetailUnscored"
      : "resolver.cannotRepairInPlaceDetail",
  );
  /** Why this card has no fix: its own reason where it has one (a repair refused for taking a
   *  facing from a product), else the shared one. */
  const cannotFixReasonOf = (flag: Flag): string => {
    try {
      const refusal = cardRefusalOf(fixSetting, readingOf(pog), flag);
      if (refusal === "belowDos") return t("resolver.cannotRepairBelowDos");
    } catch {
      // No reading: the shared reason.
    }
    return cannotFixReason;
  };
  const fixFor = (flag: Flag): PlanFix | null => {
    try {
      return cardFixOf(fixSetting, readingOf(pog), flag);
    } catch {
      return null;
    }
  };
  const draftFixFor = (flag: Flag): StudioDraft | null => {
    try {
      return draftFixOf(fixSetting, readingOf(pog), flag);
    } catch {
      return null;
    }
  };
  /** The plans each fix was made on, newest last: Undo takes the last fix back. A plan laid
   *  again from the tree starts the list over, as the fixes on the old plan are gone. */
  const [fixSteps, setFixSteps] = useState<readonly FixStep[]>(resumedFixes.fixSteps);
  const lastFix = fixSteps[fixSteps.length - 1];
  const fixesMade = fixSteps.reduce((sum, step) => sum + step.fixes, 0);
  const undoTakesFix = lastFix !== undefined && lastFix.undoDepth === session.undoDepth;
  /** The fixes Undo took back, newest last, each with the plan it made. Redo makes the newest
   *  again once the draft stands where it was made, so undo and redo keep the order the fixes
   *  and the draft's steps were taken in. A new action voids them, as it voids the draft's;
   *  the plan laid again as Undo and Redo step the draft does not. */
  const [undoneFixes, setUndoneFixes] = useState<readonly { step: FixStep; made: Pog }[]>(
    resumedFixes.undoneFixes,
  );
  useEffect(() => {
    setPanelFixes(pogAtom as object, { planBeforeFixes, fixSteps, undoneFixes });
  }, [pogAtom, planBeforeFixes, fixSteps, undoneFixes]);
  const fixToRedo = undoneFixes[undoneFixes.length - 1];
  const redoTakesFix = fixToRedo !== undefined && fixToRedo.step.undoDepth === session.undoDepth;

  /** Measured against an order the plan never settled, a blocking card is a prompt, not a
   *  breach: it is shown, and counted, as one to review. */
  const shownSeverityOf = (flag: Flag): FlagSeverity => {
    if (flag.severity !== "blocking") return flag.severity;
    const levelAttr = levelAttrForFlag(flag);
    return levelAttr && inference.confidenceByAttr[levelAttr]?.convention ? "toReview" : "blocking";
  };

  /** The blocking exceptions: tidy-ups the panel shows as Issues, a flow card aside. Those with
   *  a fix found stand on one amber line above the questions; they stay in the tidy-ups too. */
  const blockingExceptions = tidyUps.flags.filter(
    (flag) => shownSeverityOf(flag) === "blocking" && !isFlowCard(flag),
  );
  /** Those with a fix, and those whose fix is still being worked out: a line that goes while
   *  an answer's new tree is read would say the products moved when nothing did. */
  const fixableExceptions = blockingExceptions.filter(
    (flag) =>
      inPlaceRefusals[flag.id] !== true &&
      (!(flag.id in knownFixes) || Boolean(knownFixes[flag.id])),
  );
  /** The first few blocking exceptions' fixes are found off the render, as an opened card's
   *  is, so the amber line can offer "Move it" only where a move exists. */
  const exceptionsToWorkOut = blockingExceptions
    .slice(0, EXCEPTION_FIXES_WORKED_OUT)
    .filter((flag) => !(flag.id in knownFixes))
    .map((flag) => flag.id)
    .join(PAIR_IDS_SEPARATOR);
  useEffect(() => {
    if (!isEditable || fixing || exceptionsToWorkOut === "") return;
    const ids = new Set(exceptionsToWorkOut.split(PAIR_IDS_SEPARATOR));
    const timer = window.setTimeout(() => {
      const byFlag: Record<string, PlanFix | null> = {};
      const drafts: Record<string, StudioDraft | null> = {};
      for (const flag of openFindings) {
        if (!ids.has(flag.id)) continue;
        byFlag[flag.id] = fixFor(flag);
        drafts[flag.id] = byFlag[flag.id] ? null : draftFixFor(flag);
      }
      setCardFixes((current) => {
        const same = current?.plan === pog && current.setting === fixSetting;
        return {
          plan: pog,
          setting: fixSetting,
          byFlag: { ...(same ? current.byFlag : {}), ...byFlag },
          drafts: { ...(same ? current.drafts : {}), ...drafts },
        };
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [exceptionsToWorkOut, pog, fixSetting, isEditable, fixing]);

  const skuAnchors = useMemo(() => {
    const anchors = new Map<string, { uniqueId: string; location: string }>();
    pog.planogram.bays.forEach((bay, bayIndex) => {
      bay.shelves.forEach((shelf, shelfIndex) => {
        for (const item of shelf.items) {
          const sku = item.productCode || item.id;
          if (!anchors.has(sku)) {
            anchors.set(sku, {
              uniqueId: item.uniqueId,
              location: t("panel.location", { bay: bayIndex + 1, shelf: shelfIndex + 1 }),
            });
          }
        }
      });
    });
    return anchors;
  }, [pog, t]);

  const suggestionFor = (flag: Flag): string =>
    (flag.words?.fixLabel ? phraseText(flag.words.fixLabel, flagTemplate) : flag.fixLabel) ??
    (RESOLVER_FLAG_KINDS.has(flag.kind)
      ? t(
          `resolver.suggestion.${flag.kind === "orphan" || flag.kind === "split" ? "relocate" : "resequence"}`,
        )
      : t("resolver.suggestion.retag"));

  useEffect(() => {
    const pins: Record<string, CdtStudioPin> = {};
    const cards: Record<string, CdtStudioExceptionCard> = {};
    openFindings.forEach((flag, index) => {
      const anchor = skuAnchors.get(flag.anchorSkuId);
      if (!anchor) return;
      if (!pins[anchor.uniqueId]) {
        pins[anchor.uniqueId] = { number: index + 1, flagId: flag.id };
      }
      cards[flag.id] = {
        number: index + 1,
        chip: t(`severityChip.${shownSeverityOf(flag)}`),
        // The ancestry on a line of its own, as the row shows it: before the title it reads
        // as one long chain of blocks.
        context: titleOf(flag).context,
        title: titleOf(flag).title,
        detail: detailOf(flag),
        suggestion: `${t("resolver.suggested")} ${suggestionFor(flag)}`,
        location: anchor.location,
        ...evidenceOf(flag),
        // Only a card whose fix was found: a button that does nothing is no button. While Fix
        // all runs it stays, waiting with the card's other buttons (`waitReason`).
        canAutoResolve: isEditable && Boolean(knownFixes[flag.id]),
        cannotFixReason: isEditable && cannotFixOf(flag) ? cannotFixReasonOf(flag) : undefined,
        canAccept: !isFlowCard(flag),
        waitReason: fixingReason,
      };
    });
    openDisplayOffers.forEach((offer, offerIndex) => {
      const anchor = offer.anchorSkuId ? skuAnchors.get(offer.anchorSkuId) : undefined;
      if (!anchor || pins[anchor.uniqueId]) return;
      const ruleId = placementRuleIdOf(offer.bandVariant, offer.placement);
      const number = openFindings.length + offerIndex + 1;
      pins[anchor.uniqueId] = { number, flagId: ruleId };
      cards[ruleId] = {
        number,
        chip: t("placementRule.chip"),
        title: ruleTitleOf(offer),
        detail: t(offer.treeHolds ? "placementRule.detailTreeHolds" : "placementRule.detail"),
        suggestion: t("placementRule.keep"),
        location: anchor.location,
        built: null,
        want: null,
        canAutoResolve: false,
        waitReason: fixingReason,
      };
    });
    setPins((current) => (sameStudioPins(current, pins) ? current : pins));
    setCards((current) => (sameStudioCards(current, cards) ? current : cards));
  }, [
    openFindings,
    openDisplayOffers,
    skuAnchors,
    setPins,
    setCards,
    isEditable,
    knownFixes,
    cardFixes,
    cardFixesShown,
    inPlaceRefusals,
    cannotFixReason,
    fixSetting,
    pog,
    fixingReason,
    t,
  ]);
  useEffect(
    () => () => {
      setPins((current) => (Object.keys(current).length === 0 ? current : {}));
      setCards((current) => (Object.keys(current).length === 0 ? current : {}));
    },
    [setPins, setCards],
  );

  useEffect(() => {
    if (!selectedFlagId || selectionSource !== "panel") return;
    const questionFlagIds = flagIdsOfQuestionId(selectedFlagId);
    const flag = [...openFindings, ...flags].find((entry) => questionFlagIds.includes(entry.id));
    const anchorSkuId = flag?.anchorSkuId ?? selectedPlacementOffer?.anchorSkuId;
    const anchor = anchorSkuId ? skuAnchors.get(anchorSkuId) : undefined;
    if (!anchor) return;
    document
      .querySelector(`[data-id="${anchor.uniqueId}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
  }, [selectedFlagId, selectionSource, openFindings, selectedPlacementOffer, skuAnchors]);

  useEffect(() => {
    if (selectedFlagId) return;
    const skuIds = selectedNodeId
      ? session.skuIdsUnderNode(selectedNodeId)
      : selectedException
        ? selectedException.skuIds
        : [];
    const anchor = skuIds.map((skuId) => skuAnchors.get(skuId)).find(Boolean);
    if (!anchor) return;
    document
      .querySelector(`[data-id="${anchor.uniqueId}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
  }, [selectedNodeId, selectedException, selectedFlagId, skuAnchors]);

  useEffect(() => {
    if (selectionSource !== "pin" || !selectedFlagId) return;
    if (tidyUps.flags.some((flag) => flag.id === selectedFlagId)) setTidyUpsOpen(true);
  }, [selectionSource, selectedFlagId, tidyUps]);

  useEffect(() => {
    if (!actionRequest) return;
    const flag = openFindings.find((entry) => entry.id === actionRequest.flagId);
    setActionRequest(null);
    if (!isEditable || fixing) return;
    // A placement rule's pin answers it as its row does.
    const offer = flag
      ? undefined
      : openDisplayOffers.find(
          (entry) => placementRuleIdOf(entry.bandVariant, entry.placement) === actionRequest.flagId,
        );
    if (offer && actionRequest.type !== "resolve") {
      decideRule(offer, actionRequest.type === "accept" ? "keep" : "decline");
      return;
    }
    if (!flag) return;
    if (actionRequest.type === "resolve") {
      applySurgicalFix(flag);
    } else if (actionRequest.type === "accept" && !isFlowCard(flag)) {
      commit(acceptFlagHere(flag));
      setSelectedFlagId(null);
      setSelectionSource(null);
    } else {
      commit(dismissFlag(draft, flag.id));
      setSelectedFlagId(null);
      setSelectionSource(null);
    }
  }, [actionRequest, openFindings, openDisplayOffers, draft, isEditable, fixing]);

  useEffect(
    () => () => {
      gateGenerationRef.current += 1;
      setSelectedFlagId(null);
      setSelectionSource(null);
    },
    [setSelectedFlagId, setSelectionSource],
  );

  useEffect(() => {
    if (!isEditable || fixing) return;
    if (raisedCards?.plan === pog && raisedCards.setting === fixSetting) return;
    const timer = window.setTimeout(() => {
      let cards: RaisedCard[] = [];
      try {
        cards = raisedCardsOf(fixSetting, readingOf(pog));
      } catch {
        cards = [];
      }
      setRaisedCards({ plan: pog, setting: fixSetting, cards });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [pog, fixSetting, isEditable, fixing]);

  // Fix vertical blocking, and the preview of the plan built from the tree, are offered only
  // where they hold.
  useEffect(() => {
    if (!isEditable || fixing) return;
    const unfixed = planBeforeFixes ?? workingPog;
    if (
      offers?.plan === pog &&
      offers.setting === fixSetting &&
      offers.layout === fixLayout &&
      offers.unfixed === unfixed
    ) {
      return;
    }
    const timer = window.setTimeout(() => {
      const laid = stampLayout(originalPog, fixLayout, items);
      setOffers({
        plan: pog,
        setting: fixSetting,
        layout: fixLayout,
        vertical: verticalOfferOn(pog),
        standing: readingOf(pog).verdict,
        unfixed,
        judged: readingOf(unfixed).verdict,
        laid: verdictOfLayout(originalPog, fixLayout, items, laid),
        laidAsShown: samePlacements(laid, pog),
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [pog, fixSetting, fixLayout, planBeforeFixes, workingPog, isEditable, fixing]);

  useEffect(() => {
    if (!isEditable || fixing || planBeforeFixes === null) return;
    const unfixed = planBeforeFixes;
    if (
      squaredBefore?.plan === pog &&
      squaredBefore.setting === fixSetting &&
      squaredBefore.unfixed === unfixed
    ) {
      return;
    }
    let current = true;
    squaredBeforeFixesOf(fixSetting, unfixed, readingOf(pog))
      .catch(() => null)
      .then((fix) => {
        if (current) setSquaredBefore({ plan: pog, setting: fixSetting, unfixed, fix });
      });
    return () => {
      current = false;
    };
  }, [pog, fixSetting, planBeforeFixes, isEditable, fixing]);

  // The opened card's fix is found off the click, so the list first shows it working on it.
  useEffect(() => {
    if (!isEditable || fixing || !selectedFlagId || selectedFlagId in knownFixes) return;
    const flag = openFindings.find((entry) => entry.id === selectedFlagId);
    if (!flag) return;
    const timer = window.setTimeout(() => {
      const fix = fixFor(flag);
      // The tree change is offered only where the card has no fix of its own.
      const laid = fix ? null : draftFixFor(flag);
      setCardFixes((current) => {
        const same = current?.plan === pog && current.setting === fixSetting;
        return {
          plan: pog,
          setting: fixSetting,
          byFlag: { ...(same ? current.byFlag : {}), [flag.id]: fix },
          drafts: { ...(same ? current.drafts : {}), [flag.id]: laid },
        };
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [selectedFlagId, pog, fixSetting, openFindings, isEditable, fixing]);

  const pogRef = useRef(pog);
  pogRef.current = pog;

  /** Fix vertical blocking on this plan, and what its note says: offered only as a step
   *  forward (see `verticalOfferOf`). */
  const verticalOfferOn = (plan: Pog): { fix: PlanFix | null; note: string } => {
    try {
      const { result, fix, squared, cutRefusal } = verticalOfferOf(fixSetting, readingOf(plan));
      if (cutRefusal === "belowDos") return { fix: null, note: t("resolver.blockingBelowDos") };
      if (result.refusedSparse) {
        const fill = Math.round(result.refusedSparse.fillRatio * 100);
        return { fix: null, note: t("resolver.blockingTooSparse", { fill }) };
      }
      if (squared && !squared.fits) return { fix: null, note: t("resolver.blockingTooTall") };
      const standing = readingOf(plan).verdict;
      if (Number.isNaN((squared?.verdict ?? standing).score)) {
        return { fix: null, note: t("resolver.blockingUnscored") };
      }
      const scores = {
        scoreBefore: shownScore(standing),
        scoreAfter: shownScore(squared?.verdict ?? standing),
      };
      // "93 against 93" reads as a typo: at the same score, say the score stays.
      const sameScore = Number.isFinite(standing.score) && scores.scoreBefore === scores.scoreAfter;
      if (!fix) {
        // At a score no lower, the squaring was refused for what it breaks, not its score.
        const harms =
          squared !== null &&
          squared.verdict.exactScore >= standing.exactScore &&
          !holdsAgainst(squared.verdict, standing);
        return {
          fix: null,
          note: harms
            ? t("resolver.blockingHarms")
            : t(sameScore ? "resolver.blockingNoGainSameScore" : "resolver.blockingNoGain", scores),
        };
      }
      return {
        fix,
        note: blockingFixedNote(scores, result),
      };
    } catch {
      return { fix: null, note: t("resolver.blockingUnscored") };
    }
  };

  /** What squaring did, in clauses that each say something: none reads "0 dropped". */
  const blockingFixedNote = (
    scores: { scoreBefore: number | string; scoreAfter: number | string },
    result: VerticalOffer["result"],
  ): string => {
    const added = result.grown.reduce((sum, growth) => sum + growth.addedFacings, 0);
    const removed = result.shrunk.reduce((sum, shrink) => sum + shrink.removedFacings, 0);
    const skus = new Set([
      ...result.grown.map((growth) => growth.skuId),
      ...result.shrunk.map((shrink) => shrink.skuId),
    ]).size;
    // Said once the fill slack's worth of uneven edge is left.
    const ragged =
      result.raggedAfter >= planSlacksOf(runs).fill
        ? lengthLabel(result.raggedAfter, lengthUnit)
        : null;
    const addedText = t(`resolver.blockingFixedAdded_${pluralOf(added)}`, { count: added });
    const removedText = t(`resolver.blockingFixedRemoved_${pluralOf(removed)}`, {
      count: removed,
    });
    const change =
      added > 0 && removed > 0
        ? t("resolver.blockingFixedTraded", { added: addedText, removed: removedText })
        : added > 0
          ? addedText
          : removed > 0
            ? removedText
            : null;
    const dropped = result.unranged.length;
    const residuals = result.residuals.length;
    const clauses = [
      change && t(`resolver.blockingFixedOn_${pluralOf(skus)}`, { change, count: skus }),
      dropped > 0
        ? t(`resolver.blockingFixedDropped_${pluralOf(dropped)}`, { count: dropped })
        : null,
      ragged ? t("resolver.blockingFixedRagged", { ragged }) : null,
      residuals > 0
        ? t(`resolver.blockingFixedResiduals_${pluralOf(residuals)}`, { count: residuals })
        : null,
    ].filter((clause): clause is string => !!clause);
    const score = t("resolver.blockingFixed", scores);
    return clauses.length > 0 ? `${score} ${clauses.join("; ")}.` : score;
  };

  /** A fixed plan becomes the one the session works on; Reset fixes goes back to the plan
   *  from before the first fix. A fix is a new action: what Redo held is void. */
  const adoptFix = (stamped: Pog, fixes = 1) => {
    setUndoneFixes([]);
    session.clearRedo();
    standFix(
      { pog, working: workingPog, planBeforeFixes, undoDepth: session.undoDepth, fixes },
      stamped,
    );
  };

  /** The fix `step` made, standing on screen: adopted, or redone. */
  const standFix = (step: FixStep, stamped: Pog) => {
    setLastMove(null);
    setFixSteps((steps) => [...steps, step]);
    setPlanBeforeFixes((current) => current ?? workingPog);
    lastStampedPogRef.current = stamped;
    outsideEditStandsRef.current = false;
    setInPlaceRefusals({});
    // A fixed plan is the studio's own, as a preview is: close and a remount must not take it as the original.
    setPanelPreviewOn(pogAtom as object, true);
    setPog(stamped);
    setWorkingPog(stamped);
  };

  const undoFix = (step: FixStep) => {
    setLastMove(null);
    setUndoneFixes((undone) => [...undone, { step, made: pog }]);
    setFixSteps((steps) => steps.slice(0, -1));
    lastStampedPogRef.current = step.pog === originalPog ? null : step.pog;
    outsideEditStandsRef.current = false;
    setBlockingFixNote(null);
    setInPlaceRefusals({});
    setPlanBeforeFixes(step.planBeforeFixes);
    setPog(step.pog);
    setWorkingPog(step.working);
  };

  const applySurgicalFix = (flag: Flag) => {
    setBlockingFixNote(null);
    const fix = flag.id in knownFixes ? knownFixes[flag.id] : fixFor(flag);
    if (fix) {
      setLastMove(null);
      adoptFix(fix.pog);
      return;
    }
    setInPlaceRefusals((current) => ({ ...current, [flag.id]: true }));
  };

  /** A tree edit lays the plan again from the tree and discards the fixes, so it is judged
   *  against the plan from before them. Against the fixed plan nearly every edit would break
   *  a block the fixes mended, and be refused. */
  const unfixedVerdict = () => readingOf(planBeforeFixes ?? workingPog).verdict;

  /** What a tree edit costs, for the confirm modal: with fixes standing, the score on screen
   *  against the plan laid, asked whatever it is; otherwise only a lower score is asked. */
  const treeEditCostOf = (laid: PlanVerdict, onScreen: PlanVerdict, judged: PlanVerdict) =>
    fixesMade > 0
      ? { scoreBefore: shownScore(onScreen), scoreAfter: shownScore(laid) }
      : scoreCostOf(judged, laid);

  /** A tree edit that does harm is refused, the note saying why; one that only lowers the
   *  score, or discards fixes, is laid once the user accepts that. */
  const layIfHarmless = (
    next: StudioDraft,
    lay: (cost: ReturnType<typeof scoreCostOf>) => void,
    laid = verdictOfLayout(originalPog, buildLayout(items, next, runs), items),
  ) => {
    const judged = unfixedVerdict();
    if (treeEditHarms(laid, judged)) setBlockingFixNote(t(treeEditRefusalKey(laid)));
    else lay(treeEditCostOf(laid, readingOf(pog).verdict, judged));
  };

  /**
   * Every card's fix, then Fix vertical blocking and the flow sort, until nothing makes the
   * plan better (see `fixAll`). A fix made before can stand in the way of a better plan, so
   * the plan from before the fixes is worked too and the better end kept. One step for
   * Reset fixes; the plan never ends worse than it started.
   */
  const runFixAll = async () => {
    const start = pog;
    const standing = readingOf(start).verdict;
    const unfixed = planBeforeFixes ?? workingPog;
    // The arrangement a moved tree was laid in can stand in the way too: its other laying is
    // worked beside it, while the tree is the one it was laid for (a Keep or Dismiss since
    // leaves it so).
    const replayed =
      otherLaying && sameTreeForFixes(otherLaying.draft, draft) ? otherLaying.pog : null;
    setBlockingFixNote(null);
    setFixAllProgress({ step: 0 });
    try {
      const best = await fixAllOnce(
        fixSetting,
        [start, ...(unfixed !== start ? [unfixed] : []), ...(replayed ? [replayed] : [])],
        (step) => setFixAllProgress({ step }),
      );
      // The plan was replaced while the fixes were worked out: they belong to a plan gone.
      if (pogRef.current !== start) return;
      // Kept only where the tile shows the gain: Fix all never ends at the score it found.
      if (best.kept.length > 0 && raisesTile(best.after, standing)) {
        adoptFix(best.pog, best.kept.length);
        const note = fixAllNoteOf(
          t,
          best.kept.map((fix) =>
            fix.kind === "card"
              ? fixNameOf(fix.flag)
              : t(
                  fix.kind === "vertical"
                    ? "toolbar.fixBlocking"
                    : fix.kind === "fill"
                      ? "resolver.fillShelves"
                      : "resolver.flowSort",
                ),
          ),
          { scoreBefore: shownScore(standing), scoreAfter: shownScore(best.after) },
        );
        setFixAllNote(note);
        setBlockingFixNote(note);
      } else {
        setBlockingFixNote(t("resolver.fixAllNoGain", { score: shownScore(standing) }));
      }
    } catch (error) {
      console.error("Fix all could not run.", error);
      if (pogRef.current === start) {
        const reason = error instanceof Error ? error.message : String(error);
        setBlockingFixNote(t("resolver.fixAllFailed", { reason }));
      }
    } finally {
      setFixAllProgress(null);
    }
  };

  const flagActionsFor = (flag: Flag) => {
    if (isFlagResolved(flag) || !isEditable) return null;
    const known = flag.id in knownFixes;
    const canFix = Boolean(knownFixes[flag.id]) && inPlaceRefusals[flag.id] !== true;
    // Found on the tree the fixes read; made on the draft as it stands now.
    const laid = knownDrafts[flag.id] ? applyFix(draft, flag) : null;
    return (
      <Flex gap="6px" onClick={(event) => event.stopPropagation()}>
        {!known ? (
          <Waits reason={fixingReason}>
            <Button size="small" isLoading>
              {t("resolver.autoResolve")}
            </Button>
          </Waits>
        ) : canFix ? (
          <Waits reason={fixingReason}>
            <Button size="small" isDisabled={fixing} onClick={() => applySurgicalFix(flag)}>
              {t("resolver.autoResolve")}
            </Button>
          </Waits>
        ) : (
          laid && (
            <Waits reason={fixingReason}>
              {/* A tree change, laid as a tree move is: judged, weighed and asked. */}
              <Button size="small" isDisabled={fixing} onClick={() => layTreeEdit(laid)}>
                {t("evidence.fix")}
              </Button>
            </Waits>
          )
        )}
        {!isFlowCard(flag) && (
          <Waits reason={fixingReason}>
            <Button
              size="small"
              color="yellow"
              variant="borderOnly"
              isDisabled={fixing}
              onClick={() => commit(acceptFlagHere(flag))}
              // A title of its own would cover the wrapper's on hover: while it waits, the wait.
              title={
                fixingReason ??
                (acceptVariantFor(flag) === null
                  ? t("evidence.acceptNoRow")
                  : t("evidence.acceptNamesVariant", {
                      variant: plain(acceptVariantFor(flag) ?? ""),
                    }))
              }
            >
              {t("evidence.accept")}
            </Button>
          </Waits>
        )}
        <Waits reason={fixingReason}>
          <Button
            size="small"
            variant="borderless"
            isDisabled={fixing}
            onClick={() => commit(dismissFlag(draft, flag.id))}
          >
            {t("evidence.dismiss")}
          </Button>
        </Waits>
      </Flex>
    );
  };

  const undoLastStep = () => {
    if (undoTakesFix) {
      undoFix(lastFix);
      return;
    }
    // Fixes stand on the drawn plan whatever the draft decides: a decision made after
    // them steps back alone, and the next Undo takes the fix. And a Dismiss or Keep
    // undone lays nothing, as it laid nothing when it was made.
    const undone = session.undoDraft;
    if (lastFix !== undefined || (undone && sameTreeForFixes(undone, draft))) {
      session.undo();
      setLastMove(null);
      setBlockingFixNote(null);
      return;
    }
    const followUp = historyPreviewFollowUpFor("undo", previewOn, session.undoDepth, authorOfPog());
    session.undo();
    setLastMove(null);
    setBlockingFixNote(null);
    // Outside writer owns the pog: step the draft, not the atom.
    if (followUp === "stand-down") {
      lastStampedPogRef.current = null;
      setPreviewOn(false);
      return;
    }
    // Base draft must restore the original pog, not buildLayout(initialDraft).
    if (followUp === "restore-original") {
      setPreviewOn(false);
      setPog(originalPog);
      setWorkingPog(originalPog);
      return;
    }
    if (followUp === "reapply") applyPreview();
  };

  const questionsHeader =
    openAnswers.length === 0
      ? t("panel.questions.headerNone")
      : openAnswers.length === 1
        ? t("panel.questions.headerOne")
        : t("panel.questions.headerMany", { count: openAnswers.length });

  // With no brand named, "4 others" says nothing the count before it did not.
  const tidyUpsBreakdown =
    tidyUps.brands.length === 0
      ? ""
      : [
          ...tidyUps.brands.map((entry) =>
            t("panel.tidyUps.forBrand", { count: entry.count, brand: entry.brand }),
          ),
          ...(tidyUps.others > 0 ? [t("panel.tidyUps.others", { count: tidyUps.others })] : []),
        ].join(", ");

  const bandWordsOf = (offer: (typeof restrictionOffers)[number]) => {
    const words = bandWordsOfOffer(offer, items);
    return {
      section: plain(words.section),
      form: words.form === null ? t("placementRule.wholeBlock") : plain(words.form),
    };
  };
  /** Every open band a whole block: the intro says blocks, not forms. */
  const bandsAllWholeBlocks = undecidedOffers.every((offer) => !offer.promotion);
  const bandsIntroSections = (() => {
    const sections = bandSectionsOf(undecidedOffers, (skuId) => {
      const item = itemsBySku.get(skuId);
      return item ? sectionOnBaseOf(item, baselineDraft) : undefined;
    });
    return (sections ?? [])
      .map(({ section, count }) =>
        t("placementRule.bandsInSection", { count, section: plain(section) }),
      )
      .join(", ");
  })();
  const bandIsTicked = (offer: (typeof restrictionOffers)[number]) =>
    !untickedBands.has(placementRuleIdOf(offer.bandVariant, offer.placement));
  const tickedBands = undecidedOffers.filter(bandIsTicked).length;
  /** The bands decided: the card folds to one answered line in its place. */
  const decidedOffers = displayOffers.filter((offer) => ruleDecisionOf(offer) !== undefined);
  /** Change on the answered line: every band asked again, ticked as it was answered. */
  const reopenBands = () => {
    setUntickedBands(
      new Set(
        decidedOffers
          .filter((offer) => ruleDecisionOf(offer) === "decline")
          .map((offer) => placementRuleIdOf(offer.bandVariant, offer.placement)),
      ),
    );
    decideRules(decidedOffers.map((offer) => ({ offer, decision: undefined })));
  };
  /** Each card's number, given the first time it shows and never changed after: an answered
   *  card keeps its number, and the cards after it are not renumbered. */
  const cardNumbersRef = useRef(new Map<string, number>());
  const numberOf = (id: string): number => {
    const numbers = cardNumbersRef.current;
    const known = numbers.get(id);
    if (known !== undefined) return known;
    numbers.set(id, numbers.size + 1);
    return numbers.size;
  };
  /** "Keep these N": the ticked bands kept, the rest left flexible, in panel order, one step. */
  const keepTickedBands = () => {
    decideRules(
      undecidedOffers.map((offer) => ({
        offer,
        decision: bandIsTicked(offer) ? ("keep" as const) : ("decline" as const),
      })),
    );
    if (selectedPlacementOffer && undecidedOffers.includes(selectedPlacementOffer)) {
      setSelectedFlagId(null);
      setSelectionSource(null);
    }
  };
  const showRule = (ruleId: string) => {
    const next = selectedFlagId === ruleId && selectionSource === "panel" ? null : ruleId;
    setSelectedFlagId(next);
    setSelectionSource(next ? "panel" : null);
    setSelectedNodeId(null);
    setSelectedExceptionId(null);
  };

  /** The amber line's words: the one product and where it stands, or how many there are. */
  const exceptionLine = (() => {
    if (fixableExceptions.length === 0) return null;
    const tail = t("panel.exception.tail");
    if (blockingExceptions.length > 1) {
      const count = blockingExceptions.length;
      if (blockingExceptions.every((flag) => exceptionKindOf(flag) === "outside")) {
        return {
          text: `${t("panel.exception.manyOutside", { count })} ${t(
            "panel.exception.doOutsideMany",
          )}; ${tail}`,
          button: t("panel.exception.moveThemBack"),
        };
      }
      const titles = blockingExceptions.map((flag) => titleOf(flag).title);
      const shown = titles.slice(0, EXCEPTION_TITLES_SHOWN).join("; ");
      const names =
        titles.length > EXCEPTION_TITLES_SHOWN
          ? t("resolver.fixAllMore", {
              names: shown,
              count: titles.length - EXCEPTION_TITLES_SHOWN,
            })
          : shown;
      return {
        text: `${t("panel.exception.manyOther", { count, titles: names })} ${t(
          "panel.exception.doMany",
        )}; ${tail}`,
        button: t("panel.exception.fixThem"),
      };
    }
    const [flag] = fixableExceptions;
    const kind = exceptionKindOf(flag);
    const what = t("panel.exception.oneFlag", {
      title: titleOf(flag).title,
      location: skuAnchors.get(flag.anchorSkuId)?.location ?? "",
    });
    const action =
      kind === "gap"
        ? t("panel.exception.doGap")
        : kind === "outside"
          ? t("panel.exception.doOutside")
          : t("panel.exception.doOther");
    return {
      text: `${what} ${action}; ${tail}`,
      button: kind === "outside" ? t("panel.exception.moveIt") : t("panel.exception.fixIt"),
    };
  })();

  /** A card's title line: its number and title, and the chevron that folds it. */
  const cardHeaderOf = (id: string, title: string) => {
    const folded = foldedCards.has(id);
    return (
      <Flex align="start" gap="8px">
        <Flex flexGrow minWidth="0">
          <Text variant="small1" color={STUDIO_THEME.text} bold>
            {title}
          </Text>
        </Flex>
        <CardFold
          type="button"
          aria-label={t(folded ? "panel.questions.expandCard" : "panel.questions.collapseCard")}
          aria-expanded={!folded}
          data-testid="cdt-studio-card-fold"
          onClick={() => toggleCard(id)}
        >
          <Chevron $open={!folded}>
            <Icon name="chevronRight" size={12} color={STUDIO_THEME.textSecondary} />
          </Chevron>
        </CardFold>
      </Flex>
    );
  };

  const questionLocationText = (locations: NonNullable<Flag["question"]>["where"]) =>
    locations
      .map(({ run, bays }) => {
        const bayText = t(`placementRule.onBays_${pluralOf(new Set(bays).size)}`, {
          bays: shelfSpanLabel(bays),
        });
        const runText = run ? (run.name ?? t("panel.questions.run", { number: run.number })) : "";
        return runText ? `${runText} ${bayText}` : bayText;
      })
      .join(t("panel.questions.locationJoin"));

  const questionCopyOf = (question: StudioQuestion) => {
    const [first, second] = question.flags.map((flag) => flag.question);
    if (first.sizeTier) {
      const { line, from, to, shelves, ownShelves } = first.sizeTier;
      const shelvesText = (list: readonly number[]) =>
        tDynamic(`flagWords.shelves_${pluralOf(new Set(list).size)}`, {
          shelves: shelfSpanLabel(list),
        });
      return {
        title: t(
          first.skuCount === 1
            ? "panel.questions.sizeTierTitleOne"
            : "panel.questions.sizeTierTitleMany",
          { line, count: first.skuCount, from, to },
        ),
        where: t("panel.questions.sizeTierWhere", {
          where: shelvesText(shelves),
          to,
          brand: first.brand,
          from,
          mainWhere: shelvesText(ownShelves),
        }),
        yes: t("panel.questions.sizeTierYes", { to }),
        no: t("panel.questions.sizeTierNo", { from }),
        done: t("panel.questions.sizeTierDone", { line, to }),
      };
    }
    if (!second) {
      return {
        title: t(
          first.shape === "edge" ? "panel.questions.splitTitleEdge" : "panel.questions.splitTitle",
          { newBrand: first.newBrand, brand: first.brand },
        ),
        where: t(
          first.skuCount === 1 ? "panel.questions.splitWhereOne" : "panel.questions.splitWhereMany",
          {
            count: first.skuCount,
            where: questionLocationText(first.where),
            brand: first.brand,
            mainWhere: questionLocationText(first.mainWhere),
          },
        ),
        yes: t("panel.questions.splitYes"),
        no: t("panel.questions.splitNo", { brand: first.brand }),
        done: t("panel.questions.splitDone", { newBrand: first.newBrand }),
      };
    }
    const firstWhere = questionLocationText(first.where);
    const secondWhere = questionLocationText(second.where);
    const where = firstWhere === secondWhere ? firstWhere : `${firstWhere}, ${secondWhere}`;
    return {
      title: t("panel.questions.pairTitle", { first: first.newBrand, second: second.newBrand }),
      where:
        first.skuCount !== second.skuCount
          ? t("panel.questions.pairWhere", {
              firstCount: first.skuCount,
              secondCount: second.skuCount,
              where,
            })
          : t(
              first.skuCount === 1
                ? "panel.questions.pairWhereEachOne"
                : "panel.questions.pairWhereEachMany",
              { count: first.skuCount, where },
            ),
      yes: t("panel.questions.pairYes"),
      no: t("panel.questions.pairNo"),
      done: t("panel.questions.pairDone", { first: first.newBrand, second: second.newBrand }),
    };
  };

  const clearQuestionSelection = (question: StudioQuestion) => {
    if (selectedFlagId !== question.id) return;
    setSelectedFlagId(null);
    setSelectionSource(null);
  };

  /** Yes applies each offer's own fix, in one step the toolbar's Undo takes back. */
  const answerYes = (question: StudioQuestion) => {
    // A size-tag Yes is recorded with the draft, so a reload puts it back (`keptAnswersOf`).
    const draftAfter = question.flags.reduce(
      (next, flag) =>
        flag.question.sizeTier
          ? answerSizeTier(next, flag, flag.question.sizeTier.line)
          : applyFix(next, flag),
      draft,
    );
    commit(draftAfter);
    applyPreview();
    const flagIds = question.flags.map((flag) => flag.id);
    const text = questionCopyOf(question).done;
    setAnsweredQuestions((current) => [
      ...current.filter((answer) => answer.id !== question.id),
      {
        id: question.id,
        numberId: question.flags[0].question.sizeTier ? SIZE_TAGS_CARD_ID : question.id,
        flagIds,
        text,
        draftAfter,
      },
    ]);
    clearQuestionSelection(question);
  };

  const answerNo = (question: StudioQuestion) => {
    const flagIds = question.flags.map((flag) => flag.id);
    const declined = declineQuestions(questionsKey, flagIds);
    setDeclinedQuestionIds((current) => new Set([...current, ...declined]));
    clearQuestionSelection(question);
  };

  /** A size layout answer: one step, kept with the draft and past a reload. */
  const answerLayout = (question: StudioQuestion, layout: SizeLayout) => {
    const column = question.flags[0].scopePath.join(PATH_SEPARATOR);
    commit(answerSizeLayout(draft, column, layout));
    clearQuestionSelection(question);
  };

  const subBrandsText = (names: readonly string[]) =>
    names.length <= 1
      ? (names[0] ?? "")
      : `${names.slice(0, -1).join(", ")}${t("panel.questions.listAnd")}${names[names.length - 1]}`;

  /** The size layout card: one question in planogram words, three answers, none chosen. */
  const layoutCopyOf = (question: StudioQuestion) => {
    const { brand, sizeLayout } = question.flags[0].question;
    const subBrands = sizeLayout?.subBrands ?? [];
    return {
      title: t(
        sizeLayout?.sizesRunUp === false
          ? "panel.questions.sizeLayoutTitleUnsorted"
          : "panel.questions.sizeLayoutTitle",
        { brand, subBrands: subBrandsText(subBrands) },
      ),
      answers: [
        { layout: "ladder" as const, text: t("panel.questions.sizeLayoutLadder") },
        {
          layout: "horizontal" as const,
          text: t("panel.questions.sizeLayoutHorizontal", {
            order: subBrands.join(t("panel.questions.sizeLayoutAbove")),
          }),
        },
        { layout: "sizeTiers" as const, text: t("panel.questions.sizeLayoutVertical") },
      ],
    };
  };

  const layoutDoneText = (brand: string, layout: SizeLayout) =>
    t(
      layout === "ladder"
        ? "panel.questions.sizeLayoutDoneLadder"
        : layout === "horizontal"
          ? "panel.questions.sizeLayoutDoneHorizontal"
          : "panel.questions.sizeLayoutDoneVertical",
      { brand },
    );

  /** One line per answered size layout question, and per size-tag Yes kept from an earlier
   *  visit (this visit's own Yes keeps its Undo line): the answer in words, and Change. The
   *  questions are the cards' own: the as-built ones, then those only the preview raises. */
  const keptAnswerLines = [
    ...[...asBuiltFlags, ...flags.filter((flag) => !asBuiltFlags.some((a) => a.id === flag.id))]
      .filter((flag) => flag.id.startsWith(SIZE_LAYOUT_PREFIX) && flag.question?.sizeLayout)
      .flatMap((flag) => {
        const column = flag.scopePath.join(PATH_SEPARATOR);
        const layout = draft.sizeLayoutAnswers?.[column];
        return layout
          ? [
              {
                id: flag.id,
                numberId: flag.id,
                text: layoutDoneText(flag.question?.brand ?? column, layout),
                change: () => commit(clearSizeLayoutAnswer(draft, column)),
              },
            ]
          : [];
      }),
    ...Object.entries(draft.sizeTierAnswers ?? {})
      .filter(([flagId]) => !answeredLines.some((answer) => answer.flagIds.includes(flagId)))
      .map(([flagId, answer]) => ({
        id: flagId,
        numberId: SIZE_TAGS_CARD_ID,
        text: t("panel.questions.sizeTierDone", { line: answer.line, to: answer.to }),
        change: () => commit(withdrawSizeTierAnswer(draft, flagId)),
      })),
  ];

  /** Show on the hidden-questions line: asks them again and forgets the No. */
  const showHiddenQuestions = () => {
    const flagIds = hiddenQuestions.flatMap((question) => question.flags.map((flag) => flag.id));
    undeclineQuestions(questionsKey, flagIds);
    const forgotten = new Set(flagIds);
    setDeclinedQuestionIds(
      (current) => new Set([...current].filter((flagId) => !forgotten.has(flagId))),
    );
  };

  const showQuestion = (question: StudioQuestion) => {
    const next = selectedFlagId === question.id && selectionSource === "panel" ? null : question.id;
    setSelectedFlagId(next);
    setSelectionSource(next ? "panel" : null);
    setSelectedNodeId(null);
    setSelectedExceptionId(null);
  };

  const renderFindingRow = (flag: Flag, index: number) => {
    const levelAttr = levelAttrForFlag(flag);
    const cannotFix = cannotFixOf(flag);
    const evidence = evidenceOf(flag);
    const confidence = levelAttr ? inference.confidenceByAttr[levelAttr] : undefined;
    return (
      <FindingRow
        key={flag.id}
        role="button"
        $selected={selectedFlagId === flag.id}
        onClick={() => {
          const nextFlagId =
            selectedFlagId === flag.id && selectionSource === "panel" ? null : flag.id;
          setSelectedFlagId(nextFlagId);
          setSelectionSource(nextFlagId ? "panel" : null);
          setSelectedNodeId(null);
          setSelectedExceptionId(null);
        }}
      >
        <Flex align="center" gap="6px">
          {/* The convention chip below says why a blocking card reads as one
              to review. */}
          <SeverityChip $severity={shownSeverityOf(flag)}>
            {index + 1} · {t(`severityChip.${shownSeverityOf(flag)}`)}
          </SeverityChip>
          {cannotFix && (
            <SeverityChip $severity="toCheck">{t("resolver.cannotRepairInPlace")}</SeverityChip>
          )}
          {confidence?.convention && (
            <SeverityChip $severity="toReview">
              {t("inference.conventionChip", {
                pct: `${Math.round((confidence.best ?? 0) * 100)}%`,
              })}
            </SeverityChip>
          )}
          <Text variant="small2" color={STUDIO_THEME.textSecondary}>
            {[flag.levelLabel, skuAnchors.get(flag.anchorSkuId)?.location]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        </Flex>
        {titleOf(flag).context && (
          <Text variant="small2" color={STUDIO_THEME.textSecondary}>
            {titleOf(flag).context}
          </Text>
        )}
        <Text variant="small1" color={STUDIO_THEME.text} bold>
          {titleOf(flag).title}
        </Text>
        {cannotFix && (
          <Text variant="small2" color={STUDIO_THEME.textSecondary}>
            {cannotFixReasonOf(flag)}
          </Text>
        )}
        {selectedFlagId === flag.id && (
          <>
            <Text variant="small2" color={STUDIO_THEME.textSecondary}>
              {detailOf(flag)}
            </Text>
            {evidence.built && (
              <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                {t("evidence.built", { order: evidence.built })}
              </Text>
            )}
            {evidence.want && (
              <Text variant="small2" color={STUDIO_THEME.text}>
                {t("evidence.want", { order: evidence.want })}
              </Text>
            )}
            <Text variant="small2" color={STUDIO_THEME.text}>
              {t("resolver.suggested")} {suggestionFor(flag)}
            </Text>
            {flagActionsFor(flag)}
          </>
        )}
      </FindingRow>
    );
  };

  const showFindings =
    openFindings.length > 0 ||
    questions.length > 0 ||
    hiddenQuestions.length > 0 ||
    answeredLines.length > 0 ||
    keptAnswerLines.length > 0 ||
    openDisplayOffers.length > 0 ||
    decidedOffers.length > 0 ||
    shownLayoutAssumptions.length > 0;

  /** The size tags' one line, from the questions' own data: the first tag in words, the rest
   *  named. */
  const sizeTagsLineText = (() => {
    const [first, ...rest] = sizeTagQuestions.map(
      (question) => question.flags[0].question.sizeTier,
    );
    if (!first) return "";
    const line = t("panel.questions.sizeTagsLine", {
      line: first.line,
      from: first.from,
      to: first.to,
    });
    const more = rest.flatMap((tier) => (tier ? [tier.line] : []));
    return more.length > 0
      ? `${line} ${t("panel.questions.sizeTagsMore", { lines: more.join(", ") })}`
      : line;
  })();

  /** The wall's sections and brand blocks, for the fold line and the footer alike. */
  const wallCounts = wallCountsOf(items, draft, tree, brandAttr);
  const foldCounts = new Map<string, FoldCount>(
    [
      [wallCounts.brandLevel?.attr, wallCounts.brandBlocks, "blocks"] as const,
      // The top level last, so a wall whose brands are its top level counts them as sections.
      [draft.spine[0]?.attr, wallCounts.sections, "sections"] as const,
    ].flatMap(([attr, count, unit]) =>
      attr === undefined || count === null ? [] : [[attr, { count, unit }] as [string, FoldCount]],
    ),
  );
  const shipsParams = {
    sections: t(`footer.sections_${pluralOf(wallCounts.sections)}`, {
      count: wallCounts.sections,
    }),
    bands: t(`footer.shelfBands_${pluralOf(keptDisplayOffers.length)}`, {
      count: keptDisplayOffers.length,
    }),
  };

  /** What the footer says beside Finalize: what it waits on, or what it ships. */
  const footerStatus = alreadyShipped
    ? { title: t("footer.finalized"), detail: t("footer.finalizedDetail") }
    : openAnswers.length > 0
      ? {
          title: t(`footer.waiting_${pluralOf(openAnswers.length)}`, {
            count: openAnswers.length,
          }),
          detail: `${capitalized(openAnswers.join(", "))}. ${t("footer.waitingThen")}`,
        }
      : {
          title: t("footer.ready"),
          // A wall with no brand level says nothing of brand blocks, rather than "0".
          detail:
            wallCounts.brandBlocks === null
              ? t("footer.shipsNoBrands", shipsParams)
              : t("footer.ships", {
                  ...shipsParams,
                  brands: t(`footer.brandBlocks_${pluralOf(wallCounts.brandBlocks)}`, {
                    count: wallCounts.brandBlocks,
                  }),
                }),
        };

  /** A question answered Yes or No (a split, a pair, a size tag), under its title. */
  const renderAnswerCard = (question: StudioQuestion, title: string) => {
    const copy = questionCopyOf(question);
    return (
      <QuestionCard
        key={question.id}
        data-testid="cdt-studio-question"
        $selected={selectedFlagId === question.id}
      >
        {cardHeaderOf(question.id, title)}
        {!foldedCards.has(question.id) && (
          <>
            <Text variant="small2" color={STUDIO_THEME.textSecondary}>
              {copy.where}
            </Text>
            <Flex align="center" gap="6px">
              {isEditable && (
                <>
                  <Button size="small" color="success" onClick={() => answerYes(question)}>
                    {copy.yes}
                  </Button>
                  <Button size="small" variant="inverted" onClick={() => answerNo(question)}>
                    {copy.no}
                  </Button>
                </>
              )}
              <Flex flexGrow />
              <Button size="small" variant="borderless" onClick={() => showQuestion(question)}>
                {t("panel.questions.showMe")}
              </Button>
            </Flex>
          </>
        )}
      </QuestionCard>
    );
  };

  const totalBays = runs.reduce((sum, run) => sum + run.bays, 0);

  return (
    <Flex column width="100%" height="100%" minHeight="0" data-testid="cdt-studio-panel">
      <Flex
        align="center"
        gap="8px"
        padding="10px 12px"
        borderBottom={`1px solid ${STUDIO_THEME.border}`}
        style={{ position: "relative" }}
      >
        <Text variant="subtitle2" color={STUDIO_THEME.text}>
          {t("pageTitle")}
        </Text>
        <Text variant="small2" color={STUDIO_THEME.textSecondary}>
          {t("itemsSummary", {
            skus: t(`itemsSkus_${pluralOf(items.length)}`, { count: items.length }),
            bays: t(`itemsBays_${pluralOf(totalBays)}`, { count: totalBays }),
          })}
        </Text>
        <Flex flexGrow />
        {/* Balance blocks is withdrawn from the toolbar; the algorithm stays in
         * domain/balanceBlocks.ts with its own tests. */}
        <Waits reason={fixingReason}>
          <Button
            size="small"
            variant="borderless"
            isDisabled={!(session.canUndo || undoTakesFix) || !isEditable || fixing}
            name="cdt-studio-panel-undo"
            onClick={undoLastStep}
          >
            {t("toolbar.undo")}
          </Button>
        </Waits>
        <Button
          size="small"
          variant="borderless"
          name="cdt-studio-panel-more"
          ariaExpanded={moreOpen}
          onClick={() => setMoreOpen((open) => !open)}
        >
          {t("toolbar.more")}
        </Button>
        {onClose && (
          <Waits reason={fixingReason}>
            <Button
              name="cdt-studio-panel-close"
              size="small"
              variant="borderless"
              isDisabled={fixing}
              onClick={() => {
                if (isDirty) {
                  openConfirmModal({
                    title: t("panel.closeDirtyTitle"),
                    description: hasRelease
                      ? t("panel.closeDirtyDescription")
                      : `${t("panel.closeDirtyDescription")} ${t("panel.closeWithoutRelease")}`,
                    icon: { name: "alert", color: Color.yellow },
                    buttons: [
                      {
                        id: "keep",
                        label: t("panel.closeKeepEditing"),
                        color: "primary",
                        variant: "inverted",
                      },
                      {
                        id: "cdt-studio-close-discard",
                        label: t("panel.closeDiscard"),
                        color: "yellow",
                        onClick: closeStudio,
                      },
                    ],
                  });
                  return;
                }
                closeStudio();
              }}
            >
              {t("panel.close")}
            </Button>
          </Waits>
        )}
      </Flex>

      {/* More: every control the header used to carry beside Undo and Close. It stays open
       * until More is pressed again, so Fix all's progress stays in view. */}
      {moreOpen && (
        <MoreMenu data-testid="cdt-studio-more-menu">
          <Waits reason={fixingReason}>
            <Button
              size="small"
              variant="borderless"
              // A fix voids what Redo held (see `adoptFix`), so Redo is offered whenever it holds a
              // step: the draft's, or a fix Undo took back.
              isDisabled={!(session.canRedo || redoTakesFix) || !isEditable || fixing}
              name="cdt-studio-panel-redo"
              onClick={() => {
                if (redoTakesFix) {
                  setUndoneFixes((undone) => undone.slice(0, -1));
                  setBlockingFixNote(null);
                  standFix(fixToRedo.step, fixToRedo.made);
                  return;
                }
                // As Undo: a decision made after a fix, redone, leaves the fixed plan standing; and
                // a Dismiss or Keep redone lays nothing, as it laid nothing when it was made.
                const redone = session.redoDraft;
                const decisionOnly = redone && sameTreeForFixes(redone, draft);
                if (lastFix !== undefined || (decisionOnly && !previewOn)) {
                  session.redo();
                  setLastMove(null);
                  setBlockingFixNote(null);
                  return;
                }
                const followUp = historyPreviewFollowUpFor(
                  "redo",
                  previewOn,
                  session.undoDepth,
                  authorOfPog(),
                );
                session.redo();
                setLastMove(null);
                setBlockingFixNote(null);
                if (followUp === "stand-down") {
                  lastStampedPogRef.current = null;
                  setPreviewOn(false);
                  return;
                }
                if (followUp === "reapply") applyPreview();
              }}
            >
              {t("toolbar.redo")}
            </Button>
          </Waits>
          {planBeforeFixes !== null && (
            <Waits reason={fixingReason}>
              <Button
                size="small"
                variant="borderless"
                isDisabled={!isEditable || fixing}
                onClick={() => {
                  setBlockingFixNote(null);
                  setInPlaceRefusals({});
                  setFixSteps([]);
                  setUndoneFixes([]);
                  // Back to the plan the fixes were made on, whoever laid it.
                  lastStampedPogRef.current =
                    planBeforeFixes === originalPog ? null : planBeforeFixes;
                  setPog(planBeforeFixes);
                  setWorkingPog(planBeforeFixes);
                  setPlanBeforeFixes(null);
                }}
              >
                {t("resolver.resetFixes")}
              </Button>
            </Waits>
          )}
          <Waits
            reason={
              fixingReason ?? (shownVertical && !shownVertical.fix ? shownVertical.note : undefined)
            }
          >
            <Button
              name="cdt-studio-panel-fix-blocking"
              size="small"
              variant="borderless"
              isDisabled={!isEditable || fixing || !shownVertical?.fix}
              onClick={() => {
                if (!shownVertical?.fix) return;
                setLastMove(null);
                adoptFix(shownVertical.fix.pog);
                setBlockingFixNote(shownVertical.note);
              }}
            >
              {t("toolbar.fixBlocking")}
            </Button>
          </Waits>
          <Button
            size="small"
            variant="borderless"
            isDisabled={!isEditable || fixing}
            name="cdt-studio-panel-fix-all"
            onClick={() => void runFixAll()}
          >
            {/* No total: a card is tried again each time the plan changes under it. */}
            {fixAllProgress
              ? fixAllProgress.step === 0
                ? t("toolbar.fixAllStarting")
                : t(`toolbar.fixAllProgress_${pluralOf(fixAllProgress.step)}`, {
                    count: fixAllProgress.step,
                  })
              : weighingMove
                ? t("toolbar.weighingMove")
                : t("toolbar.fixAll")}
          </Button>
          {/* Resolve all is withdrawn — see KNOWN_BUGS.md #2: the batch pass in
           * studio.resolveAll() damages the plan. Per-row Auto-resolve is unaffected.
           * Finalize is not on this row: it is the footer's one primary button. */}
          <Waits reason={fixingReason}>
            <Button
              size="small"
              variant="borderOnly"
              isDisabled={!hasFinalizableTree || fixing}
              onClick={downloadCsvs}
              name="cdt-studio-panel-download-csv"
            >
              {t("toolbar.downloadCsv")}
            </Button>
          </Waits>
          <Flex align="center" gap="8px">
            <Waits reason={fixingReason ?? previewRefusal ?? undefined}>
              <Toggle
                value={previewOn}
                setValue={turnPreview}
                isDisabled={
                  !isEditable || fixing || (!previewOn && (!shownOffers || previewRefusal !== null))
                }
              />
            </Waits>
            <Text variant="small1" color={STUDIO_THEME.text}>
              {t("panel.previewRegenerated")}
            </Text>
          </Flex>
        </MoreMenu>
      )}

      {placeableStoreRange.length > 0 && (
        <Flex column gap="4px" padding="0 12px 6px 12px" data-testid="cdt-studio-store-range">
          <Flex>
            <Button
              size="small"
              variant="borderOnly"
              name="cdt-studio-store-range-toggle"
              onClick={() => setStoreRangeOpen((open) => !open)}
            >
              {t(`panel.storeRange_${pluralOf(placeableStoreRange.length)}`, {
                count: placeableStoreRange.length,
              })}
            </Button>
          </Flex>
          {storeRangeOpen &&
            (placedStoreRange ?? placeableStoreRange).map((product) => (
              <Text
                key={product.productCode}
                variant="small2"
                color={STUDIO_THEME.text}
                testId="cdt-studio-store-range-product"
              >
                {"nextTo" in product
                  ? t("panel.storeRangeProduct", {
                      name: product.name,
                      neighbour: product.nextTo.name,
                    })
                  : product.name}
              </Text>
            ))}
        </Flex>
      )}

      {(shipState.status === "refused" || shipState.status === "shipped") && (
        <Flex padding="0 12px">
          <Text
            variant="small1"
            color={
              shipState.status === "refused"
                ? STUDIO_THEME.severity.blocking.text
                : STUDIO_THEME.textSecondary
            }
            testId="cdt-studio-panel-ship-note"
          >
            {shipState.text}
          </Text>
        </Flex>
      )}

      {restrictionsRefusal !== null && (
        <Flex padding="0 12px" data-testid="cdt-studio-panel-restrictions-refusal">
          <Text variant="small1" color={STUDIO_THEME.severity.blocking.text}>
            {restrictionsRefusal}
          </Text>
        </Flex>
      )}

      {shipState.status === "shipped" && shipState.rulesUnsaved && (
        <Flex padding="0 12px" data-testid="cdt-studio-panel-rules-unsaved">
          <Text variant="small1" color={STUDIO_THEME.severity.toReview.text}>
            {t("panel.shippedWithoutRules")}
          </Text>
        </Flex>
      )}

      {shipNotices.length > 0 && (
        <Flex column gap="2px" padding="0 12px" data-testid="cdt-studio-panel-ship-notices">
          {shipNotices.map((notice) => (
            <Text key={notice} variant="small1" color={STUDIO_THEME.severity.toReview.text}>
              {notice}
            </Text>
          ))}
        </Flex>
      )}

      {shipState.status === "refused" && shipState.unplaced.length > 0 && (
        <Flex column gap="6px" padding="8px 12px" data-testid="cdt-studio-panel-unplaced-override">
          <Text variant="small1" color={STUDIO_THEME.text}>
            {t("panel.unplacedTitle", {
              count: unplacedProductCodesOf(shipState.unplaced).length,
            })}
          </Text>

          {shipState.unplaced.map((combo) => (
            <Flex column gap="2px" key={`${combo.cdt0}|${combo.cdt1}`}>
              <Text variant="small1" color={STUDIO_THEME.textSecondary}>
                {t("panel.unplacedCombo", { cdt0: combo.cdt0, cdt1: combo.cdt1 })}
              </Text>
              {combo.productCodes.map((productCode) => (
                <Text variant="small1" color={STUDIO_THEME.textSecondary} key={productCode}>
                  {t("panel.unplacedProduct", {
                    name: adapterOptions?.nameOverlay?.[productCode] ?? productCode,
                    productCode,
                  })}
                </Text>
              ))}
            </Flex>
          ))}

          <Text variant="small1" color={STUDIO_THEME.severity.blocking.text}>
            {t("panel.unplacedConsequence", {
              count: unplacedProductCodesOf(shipState.unplaced).length,
            })}
          </Text>

          <Flex>
            <Button
              size="small"
              variant="borderOnly"
              isDisabled={!isEditable}
              onClick={() => void onFinalize(true)}
              name="cdt-studio-panel-finalize-anyway"
            >
              {t("panel.unplacedContinue")}
            </Button>
          </Flex>
        </Flex>
      )}

      {gateFailure !== null && (
        <Flex padding="0 12px" data-testid="cdt-studio-panel-gate-result">
          <Text variant="small1" color={STUDIO_THEME.severity.blocking.text}>
            {t("panel.gateSaveFailed", { message: gateFailure })}
          </Text>
        </Flex>
      )}

      {blockingFixNote && (
        <Flex padding="0 12px" title={noteIsFixAll ? blockingFixNote : undefined}>
          <Text
            variant="small1"
            color={STUDIO_THEME.textSecondary}
            lineClamp={noteIsFixAll ? 1 : undefined}
          >
            {blockingFixNote}
          </Text>
        </Flex>
      )}
      <WallReadingLine
        wall={draft.wallBlocking}
        isEditable={isEditable && !fixing}
        onChoose={session.chooseWallReading}
        brandShelves={draft.brandShelves}
        onChooseBrandShelves={(choice) => commit(setBrandShelves(draft, choice))}
      />
      <StudioTreeFoldLine
        spine={draft.spine}
        counts={foldCounts}
        folded={treeFolded}
        onToggle={() => setTreeChoice(treeFolded ? "open" : "folded")}
      />
      {!treeFolded && (
        <StudioTreeCanvas
          tree={tree}
          nameOf={plain}
          spine={draft.spine}
          selectedNodeId={selectedNodeId}
          readOnly={!isEditable || fixing}
          onSelectNode={(nodeId) => {
            setSelectedNodeId(nodeId);
            setSelectedFlagId(null);
            setSelectionSource(nodeId ? "panel" : null);
          }}
          onLiftLevelHere={(scopeNodeId: string, fromLevelIndex: number, toLevelIndex: number) => {
            const result = applyHierarchyMove(
              items,
              runs,
              draft,
              { kind: "scoped-level", scopeNodeId, from: fromLevelIndex, to: toLevelIndex },
              hierarchyMoveRules,
              displayedBefore,
              renderedProjection,
              aestheticsRules,
              verticalBlockingOptions.blockShare,
              scoreScope,
            );
            commitHierarchyMove(result);
          }}
          onMoveSibling={(node: StudioTreeNode, offset) =>
            commitHierarchyMove(siblingStepOf(node, offset))
          }
          siblingStepRefusals={siblingStepRefusals}
          onMoveLevel={(from, to) => {
            const result = applyHierarchyMove(
              items,
              runs,
              draft,
              { kind: "level", from, to },
              hierarchyMoveRules,
              displayedBefore,
              renderedProjection,
              aestheticsRules,
              verticalBlockingOptions.blockShare,
              scoreScope,
            );
            commitHierarchyMove(result);
          }}
          poolingStatus={poolingStatus}
        />
      )}
      <MoveReportSummary report={moveReport} lengthUnit={lengthUnit} />
      {exceptionLine && (
        <ExceptionLine data-testid="cdt-studio-exception-line">
          <Text variant="small1" color={STUDIO_THEME.text}>
            {exceptionLine.text}
          </Text>
          {isEditable && (
            <Waits reason={fixingReason}>
              <Button
                size="small"
                variant="borderOnly"
                name="cdt-studio-exception-move"
                isDisabled={fixing}
                onClick={() =>
                  blockingExceptions.length > 1
                    ? void runFixAll()
                    : applySurgicalFix(fixableExceptions[0])
                }
              >
                {exceptionLine.button}
              </Button>
            </Waits>
          )}
        </ExceptionLine>
      )}
      {treeFolded && !showFindings && <Flex flexGrow />}

      {showFindings && (
        <FindingsRegion data-testid="cdt-studio-findings-region" $fill={treeFolded}>
          <Flex flexShrink={0} padding="8px 12px 4px 12px">
            <Text
              variant="small2"
              color={STUDIO_THEME.textSecondary}
              bold
              testId="cdt-studio-questions-header"
            >
              {questionsHeader}
            </Text>
          </Flex>
          <FindingsScroller data-testid="cdt-studio-panel-findings">
            {answeredLines.map((answer) => {
              // Undo on the line takes back this answer only while it is the latest step: any
              // later edit, fix or not, commits a new draft; undoing that edit restores this one.
              const canUndo =
                isEditable &&
                !fixing &&
                !undoTakesFix &&
                session.canUndo &&
                draft === answer.draftAfter;
              return (
                <Flex
                  key={answer.id}
                  align="center"
                  gap="6px"
                  data-testid="cdt-studio-question-answered"
                >
                  <Text variant="small1" color={STUDIO_THEME.text}>
                    {`${t("panel.questions.numbered", {
                      number: numberOf(answer.numberId),
                      title: answer.text,
                    })}${canUndo ? " ·" : ""}`}
                  </Text>
                  {canUndo && (
                    <Button size="small" variant="borderless" onClick={undoLastStep}>
                      {t("panel.questions.undo")}
                    </Button>
                  )}
                </Flex>
              );
            })}
            {keptAnswerLines.map((line) => (
              <Flex
                key={line.id}
                align="center"
                gap="6px"
                data-testid="cdt-studio-question-answered"
              >
                <Text variant="small1" color={STUDIO_THEME.text}>
                  {`${t("panel.questions.numbered", {
                    number: numberOf(line.numberId),
                    title: line.text,
                  })}${isEditable ? " ·" : ""}`}
                </Text>
                {isEditable && (
                  <Button
                    size="small"
                    variant="borderless"
                    isDisabled={fixing}
                    onClick={line.change}
                  >
                    {t("panel.questions.change")}
                  </Button>
                )}
              </Flex>
            ))}
            {layoutQuestions.map((question) => {
              const layoutCopy = layoutCopyOf(question);
              const picked = layoutPicks[question.id];
              return (
                <QuestionCard
                  key={question.id}
                  data-testid="cdt-studio-question"
                  $selected={selectedFlagId === question.id}
                >
                  {cardHeaderOf(
                    question.id,
                    t("panel.questions.numbered", {
                      number: numberOf(question.id),
                      title: layoutCopy.title,
                    }),
                  )}
                  {!foldedCards.has(question.id) && (
                    <>
                      {isEditable && (
                        <Flex column gap="4px" role="radiogroup">
                          {layoutCopy.answers.map((answer) => (
                            <AnswerRow key={answer.layout}>
                              <input
                                type="radio"
                                name={`cdt-studio-layout-${question.id}`}
                                checked={picked === answer.layout}
                                disabled={fixing}
                                onChange={() =>
                                  setLayoutPicks((current) => ({
                                    ...current,
                                    [question.id]: answer.layout,
                                  }))
                                }
                              />
                              <Text variant="small1" color={STUDIO_THEME.text}>
                                {answer.text}
                              </Text>
                            </AnswerRow>
                          ))}
                        </Flex>
                      )}
                      <Flex align="center" gap="6px">
                        {isEditable && (
                          <Waits reason={fixingReason}>
                            <Button
                              size="small"
                              variant="borderOnly"
                              name="cdt-studio-layout-confirm"
                              isDisabled={fixing || picked === undefined}
                              onClick={() => {
                                if (picked === undefined) return;
                                // Asked again after a Change, the card starts with nothing picked.
                                setLayoutPicks((current) =>
                                  Object.fromEntries(
                                    Object.entries(current).filter(([id]) => id !== question.id),
                                  ),
                                );
                                answerLayout(question, picked);
                              }}
                            >
                              {t("panel.questions.confirm")}
                            </Button>
                          </Waits>
                        )}
                        <Flex flexGrow />
                        <Button
                          size="small"
                          variant="borderless"
                          onClick={() => showQuestion(question)}
                        >
                          {t("panel.questions.showMe")}
                        </Button>
                      </Flex>
                    </>
                  )}
                </QuestionCard>
              );
            })}
            {splitQuestions.map((question) =>
              renderAnswerCard(
                question,
                t("panel.questions.numbered", {
                  number: numberOf(question.id),
                  title: questionCopyOf(question).title,
                }),
              ),
            )}
            {hiddenQuestions.length > 0 && (
              <Flex align="center" gap="6px" data-testid="cdt-studio-questions-hidden">
                <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                  {`${
                    hiddenQuestions.length === 1
                      ? t("panel.questions.hiddenOne")
                      : t("panel.questions.hiddenMany", { count: hiddenQuestions.length })
                  }${isEditable ? " ·" : ""}`}
                </Text>
                {isEditable && (
                  <Button size="small" variant="borderless" onClick={showHiddenQuestions}>
                    {t("panel.questions.show")}
                  </Button>
                )}
              </Flex>
            )}
            {undecidedOffers.length > 0 && (
              <QuestionCard
                data-testid="cdt-studio-bands"
                $selected={
                  selectedPlacementOffer !== undefined &&
                  undecidedOffers.includes(selectedPlacementOffer)
                }
              >
                {cardHeaderOf(
                  BANDS_CARD_ID,
                  t("panel.questions.numbered", {
                    number: numberOf(BANDS_CARD_ID),
                    title: t(`placementRule.bandsTitle_${pluralOf(undecidedOffers.length)}`, {
                      count: undecidedOffers.length,
                    }),
                  }),
                )}
                {!foldedCards.has(BANDS_CARD_ID) && (
                  <>
                    <Text variant="small1" color={STUDIO_THEME.text}>
                      {t(
                        bandsAllWholeBlocks
                          ? `placementRule.bandsIntroWholeBlocks_${pluralOf(undecidedOffers.length)}`
                          : bandsIntroSections === ""
                            ? `placementRule.bandsIntroNoSections_${pluralOf(undecidedOffers.length)}`
                            : `placementRule.bandsIntro_${pluralOf(undecidedOffers.length)}`,
                        { count: undecidedOffers.length, sections: bandsIntroSections },
                      )}
                    </Text>
                    <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                      {t("placementRule.bandsExplain")}
                    </Text>
                    <BandGrid>
                      <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                        {t("placementRule.bandsColumnBand")}
                      </Text>
                      <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                        {t("placementRule.bandsColumnStays")}
                      </Text>
                      <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                        {t("placementRule.bandsColumnAcross")}
                      </Text>
                      <span />
                    </BandGrid>
                    {undecidedOffers.map((offer) => {
                      const ruleId = placementRuleIdOf(offer.bandVariant, offer.placement);
                      const shelves = offer.placement.cells.map((cell) => cell.shelf);
                      const bays = offer.placement.cells.flatMap((cell) => cell.bays);
                      const { section, form } = bandWordsOf(offer);
                      return (
                        <BandRow
                          key={ruleId}
                          data-testid="cdt-studio-placement-rule"
                          $selected={selectedFlagId === ruleId}
                        >
                          <BandGrid>
                            <BandName>
                              <input
                                type="checkbox"
                                checked={bandIsTicked(offer)}
                                disabled={!isEditable || fixing}
                                onChange={(event) => {
                                  const ticked = event.target.checked;
                                  setUntickedBands((current) => {
                                    const next = new Set(current);
                                    if (ticked) next.delete(ruleId);
                                    else next.add(ruleId);
                                    return next;
                                  });
                                }}
                              />
                              <Text variant="small1" color={STUDIO_THEME.text}>
                                {section !== "" && `${section} · `}
                                <strong>{form}</strong>
                              </Text>
                            </BandName>
                            <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                              {t(`placementRule.staysOn_${pluralOf(new Set(shelves).size)}`, {
                                shelves: shelfSpanLabel(shelves, rangeOf),
                              })}
                            </Text>
                            <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                              {bays.length > 0 &&
                                t(`placementRule.onBays_${pluralOf(new Set(bays).size)}`, {
                                  bays: shelfSpanLabel(bays, rangeOf),
                                })}
                            </Text>
                            <Button
                              size="small"
                              variant="borderless"
                              name="cdt-studio-band-show"
                              onClick={() => showRule(ruleId)}
                            >
                              {t("panel.questions.showMe")}
                            </Button>
                          </BandGrid>
                          {selectedFlagId === ruleId && (
                            <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                              {t(
                                offer.treeHolds
                                  ? "placementRule.detailTreeHolds"
                                  : "placementRule.detail",
                              )}{" "}
                              {t(`placementRule.cost_${pluralOf(offer.cost.cells)}`, {
                                cells: offer.cost.cells,
                                siblings: offer.cost.siblingVariants,
                                evictions: offer.cost.evictions,
                              })}
                              {offer.promotion
                                ? ` ${t("placementRule.promotionNote", {
                                    variant: offer.bandVariant,
                                    parent: offer.promotion.parentValue,
                                  })}`
                                : ""}
                            </Text>
                          )}
                        </BandRow>
                      );
                    })}
                    <Flex align="center" gap="8px" style={{ flexWrap: "wrap" }}>
                      {isEditable && (
                        <Waits reason={fixingReason}>
                          <Button
                            size="small"
                            variant="borderOnly"
                            name="cdt-studio-bands-keep"
                            isDisabled={fixing}
                            onClick={keepTickedBands}
                          >
                            {tickedBands === 0
                              ? t("placementRule.bandsKeepNone")
                              : t(`placementRule.bandsKeep_${pluralOf(tickedBands)}`, {
                                  count: tickedBands,
                                })}
                          </Button>
                        </Waits>
                      )}
                      {isEditable && (
                        <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                          {t("placementRule.bandsKeepNote")}
                        </Text>
                      )}
                    </Flex>
                  </>
                )}
              </QuestionCard>
            )}
            {decidedOffers.length > 0 && (
              <QuestionCard data-testid="cdt-studio-bands-answered" $selected={false}>
                <Flex align="center" gap="6px">
                  <Text variant="small1" color={STUDIO_THEME.text}>
                    {`${(() => {
                      const kept = decidedOffers.filter(
                        (offer) => ruleDecisionOf(offer) === "keep",
                      ).length;
                      const flexible = decidedOffers.length - kept;
                      const words = [
                        t(`placementRule.bandsKept_${pluralOf(kept)}`, { count: kept }),
                        ...(flexible > 0
                          ? [
                              t(`placementRule.bandsFlexible_${pluralOf(flexible)}`, {
                                count: flexible,
                              }),
                            ]
                          : []),
                      ].join(", ");
                      // The card's number stays with its answer; beside an open card it has none.
                      return undecidedOffers.length === 0
                        ? t("panel.questions.numbered", {
                            number: numberOf(BANDS_CARD_ID),
                            title: words,
                          })
                        : words;
                    })()}${isEditable ? " ·" : ""}`}
                  </Text>
                  {isEditable && (
                    <Waits reason={fixingReason}>
                      <Button
                        size="small"
                        variant="borderless"
                        name="cdt-studio-bands-change"
                        isDisabled={fixing}
                        onClick={reopenBands}
                      >
                        {t("placementRule.bandsChange")}
                      </Button>
                    </Waits>
                  )}
                </Flex>
              </QuestionCard>
            )}
            {sizeTagQuestions.length > 0 && (
              <SizeTagsLine data-testid="cdt-studio-size-tags">
                <Flex align="center" gap="8px">
                  <Flex flexGrow minWidth="0">
                    <Text variant="small1" color={STUDIO_THEME.text} bold>
                      {t("panel.questions.numbered", {
                        number: numberOf(SIZE_TAGS_CARD_ID),
                        title: t(
                          `panel.questions.sizeTagsTitle_${pluralOf(sizeTagQuestions.length)}`,
                          { count: sizeTagQuestions.length },
                        ),
                      })}
                    </Text>
                  </Flex>
                  <Button
                    size="small"
                    variant="borderless"
                    name="cdt-studio-size-tags-toggle"
                    ariaExpanded={sizeTagsOpen}
                    onClick={() => setSizeTagsOpen((open) => !open)}
                  >
                    {sizeTagsOpen
                      ? t("panel.questions.sizeTagsClose")
                      : t("panel.questions.sizeTagsOpen")}
                  </Button>
                </Flex>
                <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                  {sizeTagsLineText}
                </Text>
              </SizeTagsLine>
            )}
            {sizeTagsOpen &&
              sizeTagQuestions.map((question) =>
                renderAnswerCard(question, questionCopyOf(question).title),
              )}
            {tidyUps.flags.length > 0 && (
              <TidyUpsRow
                type="button"
                data-testid="cdt-studio-tidy-ups"
                aria-expanded={tidyUpsOpen}
                onClick={() => setTidyUpsOpen((open) => !open)}
              >
                <Chevron $open={tidyUpsOpen}>
                  <Icon name="chevronRight" size={12} color={STUDIO_THEME.textSecondary} />
                </Chevron>
                <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                  {[
                    tidyUps.flags.length === 1
                      ? t("panel.tidyUps.summaryOne")
                      : t("panel.tidyUps.summaryMany", { count: tidyUps.flags.length }),
                    tidyUpsBreakdown,
                    t("panel.tidyUps.detail"),
                  ]
                    .filter((part) => part !== "")
                    .join(" · ")}
                </Text>
              </TidyUpsRow>
            )}
            {tidyUpsOpen && tidyUps.flags.map((flag, index) => renderFindingRow(flag, index))}
            {shownLayoutAssumptions.length > 0 && (
              <Flex padding="8px 0 2px 0">
                <Text variant="subtitle2" color={STUDIO_THEME.text}>
                  {t("inference.exceptionsTitle", { count: shownLayoutAssumptions.length })}
                </Text>
              </Flex>
            )}
            {shownLayoutAssumptions.map((exception: InferenceException, index) => (
              <FindingRow
                key={exception.id}
                role="button"
                $selected={selectedExceptionId === exception.id}
                onClick={() => {
                  const nextExceptionId =
                    selectedExceptionId === exception.id ? null : exception.id;
                  setSelectedExceptionId(nextExceptionId);
                  setSelectedFlagId(null);
                  setSelectionSource(nextExceptionId ? "panel" : null);
                  setSelectedNodeId(null);
                }}
              >
                <Flex align="center" gap="6px">
                  <SeverityChip
                    $severity={ALERT_KINDS.has(exception.kind) ? "toReview" : "toCheck"}
                  >
                    E{index + 1} ·{" "}
                    {ALERT_KINDS.has(exception.kind)
                      ? t("inference.alertChip")
                      : t("inference.exceptionChip")}
                  </SeverityChip>
                  <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                    {t(`inference.kind.${exception.kind}`)}
                  </Text>
                </Flex>
                <Text variant="small1" color={STUDIO_THEME.text} bold>
                  {exception.title}
                </Text>
                {selectedExceptionId === exception.id && (
                  <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                    {exception.detail}
                  </Text>
                )}
              </FindingRow>
            ))}
          </FindingsScroller>
        </FindingsRegion>
      )}
      <PanelFooter data-testid="cdt-studio-footer">
        <Flex column gap="2px" flexGrow minWidth="0">
          <Text variant="small1" color={STUDIO_THEME.text} bold testId="cdt-studio-footer-status">
            {footerStatus.title}
          </Text>
          <Text
            variant="small2"
            color={STUDIO_THEME.textSecondary}
            testId="cdt-studio-footer-detail"
          >
            {footerStatus.detail}
          </Text>
        </Flex>
        <Waits reason={finalizeWaitReason}>
          <Button
            color="success"
            isDisabled={!isEditable || finalizeBlocked}
            onClick={() => void onFinalize()}
            name="cdt-studio-panel-finalize"
          >
            {shipState.status === "shipping" ? t("panel.shipping") : t("footer.finalize")}
          </Button>
        </Waits>
      </PanelFooter>
    </Flex>
  );
};

export const CdtStudioPanel = ({
  pogAtom,
  isEditable,
  merchflowId,
  basePogId,
  categoryConfiguration: initialCategoryConfiguration,
  onClose,
  onGenerate,
}: CdtStudioPanelProps) => {
  const { t } = useTranslation("cdtStudio");
  const { data, isLoading, isError } = useStudioAttributesQuery(merchflowId);
  // Every length the studio says is in the category's unit, so it opens once that is known.
  const lengthUnitQuery = useCategoryLengthUnit(merchflowId);
  // The host clones this wrapper every planogram render. Compile from the
  // profile *contents*, not the wrapper identity, or the session rebuilds
  // and writes highlight atoms until the tab is closed.
  const profileFingerprint =
    initialCategoryConfiguration.profileJson ??
    JSON.stringify(initialCategoryConfiguration.profile);
  const configuration = useMemo(
    () =>
      data ? namedStudioConfigurationOf(initialCategoryConfiguration.profile, data) : undefined,
    [data, profileFingerprint],
  );

  if (isLoading) {
    return (
      <Flex flexGrow align="center" justify="center">
        <Spinner size="big" label={t("panel.loadingAttributes")} isCentered />
      </Flex>
    );
  }
  if (lengthUnitQuery.isError) {
    return (
      <Flex flexGrow column align="center" justify="center" gap="8px" padding="16px">
        <Text variant="small1" color={STUDIO_THEME.severity.blocking.text}>
          {t("panel.lengthUnitFailed")}
        </Text>
        <Button size="small" onClick={lengthUnitQuery.retry}>
          {t("panel.retry")}
        </Button>
      </Flex>
    );
  }
  if (lengthUnitQuery.unit === undefined) {
    return (
      <Flex flexGrow align="center" justify="center">
        <Spinner size="big" label={t("panel.loadingLengthUnit")} isCentered />
      </Flex>
    );
  }
  if (isError || !data) {
    return (
      <Flex flexGrow align="center" justify="center" padding="16px">
        <Text variant="small1" color={STUDIO_THEME.severity.blocking.text}>
          {t("panel.attributesFailed")}
        </Text>
      </Flex>
    );
  }
  if (!configuration) return null;

  return (
    <StudioPanelSession
      pogAtom={pogAtom}
      isEditable={isEditable}
      merchflowId={merchflowId}
      basePogId={basePogId ?? null}
      categoryId={data.categoryId}
      onClose={onClose}
      onGenerate={onGenerate}
      configuration={configuration}
      lengthUnit={lengthUnitQuery.unit}
    />
  );
};
